<template>
  <div class="rip">

    <!-- Loading -->
    <div v-if="loading" class="rip-loading">
      <span class="rip-spinner"></span>
      <span>{{ $t("reservationInvoicePanel.loadingVerifying") }}</span>
    </div>

    <template v-else>

      <!-- ══ FACTURAS EMITIDAS (listado) ═══════════════════════════════════ -->
      <!-- Se oculta si el padre lo muestra arriba (antes de los cargos). -->
      <div v-if="!hideEmittedList && emittedInvoices.length" class="rip-issued">
        <p class="rip-issued__title">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15l2 2 4-4"/></svg>
          {{ $t("reservationInvoicePanel.issued.title") }}
          <span class="rip-issued__count">{{ emittedInvoices.length }}</span>
        </p>
        <div class="rip-issued__list">
          <div
            v-for="inv in emittedInvoices"
            :key="inv.id"
            class="rip-issued__row"
            :class="{ 'rip-issued__row--void': inv.status === 'voided' }"
          >
            <div class="rip-issued__body">
              <div class="rip-issued__head">
                <span class="rip-status-chip" :class="`rip-status-chip--${statusClassFor(inv)}`">
                  <span class="rip-status-chip__dot"></span>
                  {{ statusLabelFor(inv) }}
                </span>
                <span v-if="inv.display_code" class="rip-inv-code">{{ inv.display_code }}</span>
                <span
                  v-if="inv.is_correction && inv.corrected_display_code"
                  class="rip-badge rip-badge--correction"
                >
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  {{ $t("reservationInvoicePanel.issued.rectifOf", { code: inv.corrected_display_code }) }}
                </span>
              </div>
              <p class="rip-issued__client">{{ inv.customer_name }}</p>
              <div class="rip-issued__meta">
                <span v-if="inv.customer_tax_id">{{ inv.customer_tax_id }}</span>
                <span>{{ formatDate(inv.created_at) }}</span>
              </div>
            </div>
            <div class="rip-issued__aside">
              <span v-if="inv.room_label" class="rip-room-chip rip-room-chip--sm">{{ inv.room_label }}</span>
              <span class="rip-issued__total">{{ inv.currency }} {{ formatNum(inv.total) }}</span>
              <div class="rip-issued__actions">
                <a v-if="inv.pdf_url" :href="inv.pdf_url" target="_blank" class="rip-icon-btn" :title="$t('reservationInvoicePanel.issued.viewPdf')">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </a>
                <button v-if="canInvoice && inv.status === 'sent'" class="rip-icon-btn rip-icon-btn--wa" @click="openWhatsappConfirmModal(inv)" :title="$t('reservationInvoicePanel.issued.sendByWhatsapp')">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </button>
                <button v-if="inv.status === 'sent' && !inv.is_correction" class="rip-icon-btn" @click="openCorrectionModal(inv)" :title="$t('reservationInvoicePanel.issued.correctTitle')">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                </button>
                <button v-if="inv.status === 'sent' && !inv.is_correction" class="rip-icon-btn rip-icon-btn--danger" @click="openVoidModal(inv)" :title="$t('reservationInvoicePanel.issued.voidTitle')">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ══ SIN FACTURA / SOLICITADA POR EL HUÉSPED ═══════════════════════ -->
      <div v-if="!invoice && !emittedInvoices.length" class="rip-empty" :class="{ 'rip-empty--requested': invoiceRequest }">
        <div class="rip-empty__info">
          <span class="rip-dot" :class="invoiceRequest ? 'rip-dot--requested' : 'rip-dot--neutral'"></span>
          <div>
            <p class="rip-empty__title">
              <template v-if="invoiceRequest">{{ $t("reservationInvoicePanel.empty.guestRequested") }}</template>
              <template v-else>{{ $t("reservationInvoicePanel.empty.noInvoice") }}</template>
              <span v-if="invoiceRequest" class="rip-req-badge">
                {{ invoiceRequest.sent_at ? $t("reservationInvoicePanel.empty.linkSent") : $t("reservationInvoicePanel.empty.requested") }}
              </span>
            </p>
            <p class="rip-empty__sub">
              <template v-if="invoiceRequest">{{ $t("reservationInvoicePanel.empty.requestedSub") }}</template>
              <template v-else>{{ $t("reservationInvoicePanel.empty.sub") }}</template>
            </p>
          </div>
        </div>

        <!-- Datos fiscales que el huésped adjuntó al solicitar la factura en el motor -->
        <div v-if="invoiceRequest && requestFiscal.length" class="rip-req-fiscal">
          <div v-for="(f, i) in requestFiscal" :key="'rf-' + i" class="rip-req-fiscal__row">
            <span class="rip-req-fiscal__k">{{ f.label }}</span>
            <span class="rip-req-fiscal__v">{{ f.value }}</span>
          </div>
        </div>

        <div v-if="canInvoice" class="rip-empty__actions">
          <!-- Copiar link -->
          <button class="rip-btn rip-btn--icon" @click="copyInvoiceLink" :disabled="generatingLink" :title="$t('reservationInvoicePanel.empty.copyLinkTitle')">
            <span v-if="generatingLink && linkAction === 'copy'" class="rip-spinner rip-spinner--sm"></span>
            <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          </button>
          <!-- Enviar link al huésped → SIEMPRE con confirmación (dispara un WhatsApp real) -->
          <button class="rip-btn rip-btn--ghost" @click="sendLinkModal.open = true" :disabled="generatingLink">
            <span v-if="generatingLink && linkAction === 'send'" class="rip-spinner rip-spinner--sm"></span>
            <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            {{ $t("reservationInvoicePanel.empty.sendLink") }}
          </button>
          <!-- Crear factura → abre el modal para rellenar datos → crea un BORRADOR -->
          <button class="rip-btn rip-btn--brand" @click="openCreateModal" :disabled="generatingLink">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
            {{ $t("reservationInvoicePanel.empty.createInvoice") }}
          </button>
        </div>
        <p v-else class="rip-empty__hint">{{ $t("reservationInvoicePanel.empty.configureHint") }}</p>
      </div>

      <!-- ══ CARGO POSTERIOR → CREAR OTRA FACTURA (colapsado) ══════════════ -->
      <div v-if="!invoice && emittedInvoices.length" class="rip-next">
        <div class="rip-next__icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
        </div>
        <div class="rip-next__text">
          <p class="rip-next__title">{{ $t("reservationInvoicePanel.next.title") }}</p>
          <p class="rip-next__sub">{{ $t("reservationInvoicePanel.next.sub") }}</p>
        </div>
        <button v-if="canInvoice" class="rip-btn rip-btn--brand rip-next__btn" @click="abrirNuevaFactura" :disabled="creatingDraft">
          <span v-if="creatingDraft" class="rip-spinner rip-spinner--sm rip-spinner--white"></span>
          <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          {{ $t("reservationInvoicePanel.next.button") }}
        </button>
        <p v-else class="rip-empty__hint">{{ $t("reservationInvoicePanel.empty.configureHint") }}</p>
      </div>

      <!-- ══ CON FACTURA (borrador / emitiéndose / error) ══════════════════ -->
      <div v-if="invoice" class="rip-card">

        <!-- ── Cabecera: estado + número + PDF ─────────────────────────── -->
        <div class="rip-card__head">
          <div class="rip-head__left">
            <span class="rip-status-chip" :class="`rip-status-chip--${statusClass}`">
              <span class="rip-status-chip__dot"></span>
              {{ statusLabel }}
            </span>
            <span v-if="invoice.display_code" class="rip-inv-code">
              {{ invoice.display_code }}
            </span>
            <span v-else-if="invoice.draft_reference" class="rip-inv-code rip-inv-code--draft"
                  :title="$t('reservationInvoicePanel.card.draftRefTitle')">
              {{ invoice.draft_reference }}
            </span>
            <!-- De qué habitación es. Sin esto, tres facturas de la misma reserva se
                 ven idénticas y no se sabe cuál es la de quién. -->
            <span v-if="invoice.room_label" class="rip-room-chip">{{ invoice.room_label }}</span>
            <span
              v-if="invoice.is_correction && invoice.corrected_display_code"
              class="rip-badge rip-badge--correction"
            >
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
              {{ $t("reservationInvoicePanel.issued.rectifOf", { code: invoice.corrected_display_code }) }}
            </span>
          </div>
          <a v-if="invoice.pdf_url" :href="invoice.pdf_url" target="_blank" class="rip-pdf-btn">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            PDF
          </a>
        </div>

        <!-- ── Cuerpo: nombre + meta + total ───────────────────────────── -->
        <div class="rip-card__body">
          <div class="rip-card__client">
            <p class="rip-client-name">{{ invoice.customer_name }}</p>
            <div class="rip-client-meta">
              <span v-if="invoice.customer_tax_id" class="rip-meta-pill">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                {{ invoice.customer_tax_id }}
              </span>
              <span v-if="invoice.customer_email" class="rip-meta-pill">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                {{ invoice.customer_email }}
              </span>
              <span v-if="invoice.created_at" class="rip-meta-pill">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                {{ formatDate(invoice.created_at) }}
              </span>
              <span v-if="invoice.payment_method_label" class="rip-meta-pill rip-meta-pill--payment">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                {{ invoice.payment_method_label }}
              </span>
            </div>
          </div>
          <div v-if="invoice.total" class="rip-card__total">
            <span class="rip-total-cur">{{ invoice.currency }}</span>
            <span class="rip-total-num">{{ formatNum(invoice.total) }}</span>
          </div>
        </div>

        <!-- ── Conceptos del documento ──────────────────────────────────────
             La queja era real: se veían los datos del cliente pero no LO QUE SE
             FACTURA. Mismo aspecto que el detalle de las facturas emitidas, más
             cantidad y precio unitario, que es lo que se revisa al cuadrar. Aquí
             van siempre a la vista (sin desplegable): el borrador existe PARA
             revisarlo, y emitir quema número. -->
        <div v-if="draftConcepts.length" class="rip-concepts">
          <p class="rip-concepts__title">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
            {{ $t("reservationInvoicePanel.concepts.title") }}
            <span class="rip-concepts__count">{{ draftConcepts.length }}</span>
          </p>

          <div class="rip-concepts__head">
            <span>{{ $t("reservationInvoicePanel.concepts.item") }}</span>
            <span>{{ $t("reservationInvoicePanel.concepts.qty") }}</span>
            <span>{{ $t("reservationInvoicePanel.concepts.unitPrice") }}</span>
            <span>{{ $t("reservationInvoicePanel.concepts.vat") }}</span>
            <span>{{ $t("reservationInvoicePanel.concepts.amount") }}</span>
          </div>

          <div v-for="(c, i) in draftConcepts" :key="'dc-' + i" class="rip-concept">
            <span class="rip-concept__name">
              {{ c.description }}
              <span v-if="c.date" class="rip-concept__date">{{ c.date }}</span>
            </span>
            <span class="rip-concept__num">{{ formatQty(c.quantity) }}</span>
            <span class="rip-concept__num">{{ formatUnit(c.unit_price) }}</span>
            <!-- El IVA se enseña porque el precio unitario va SIN él y el importe CON
                 él: sin esta columna, «4 × 91,00 = 400,40» parece una cuenta mal
                 hecha y no se puede cuadrar la línea. -->
            <span class="rip-concept__num rip-concept__vat">{{ formatVat(c.vat_rate) }}</span>
            <span class="rip-concept__num rip-concept__amount"
                  :class="{ 'rip-concept__amount--neg': c.discount }">
              {{ formatNum(c.total_with_vat) }}
            </span>
          </div>

          <div class="rip-concepts__foot">
            <span class="rip-concepts__foot-label">{{ $t("reservationInvoicePanel.concepts.linesTotal") }}</span>
            <span class="rip-concepts__foot-total">{{ invoice.currency }} {{ formatNum(conceptsTotal) }}</span>
          </div>

          <p v-if="conceptsMismatch" class="rip-concepts__warn">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0;margin-top:1px"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><circle cx="12" cy="17" r="1" fill="currentColor"/></svg>
            {{ $t("reservationInvoicePanel.concepts.mismatch", {
              lines: invoice.currency + ' ' + formatNum(conceptsTotal),
              total: invoice.currency + ' ' + formatNum(invoice.total),
            }) }}
          </p>
          <p v-else class="rip-concepts__hint">{{ $t("reservationInvoicePanel.concepts.foot") }}</p>
        </div>

        <!-- ── Cobro: lo pagado y lo que queda a deber ──────────────────── -->
        <!-- 🚨 Estas cifras NO se calculan aquí: vienen del backend, del mismo código
             que arma el PDF. Mientras no existió este bloque, el panel cerraba con
             «Suma de conceptos 195,00» y se callaba, mientras el PDF decía «Total
             adeudado 175,00». Dos cuentas del mismo documento, y el anfitrión sin
             saber cuál era la buena. -->
        <div v-if="invoice.cobro" class="rip-cobro">
          <div class="rip-cobro__row">
            <span>{{ $t("reservationInvoicePanel.cobro.total") }}</span>
            <span class="rip-cobro__num">{{ invoice.currency }} {{ formatNum(invoice.cobro.total) }}</span>
          </div>

          <div v-for="(p, i) in invoice.cobro.detalle" :key="'cb-' + i"
               class="rip-cobro__row rip-cobro__row--paid">
            <span>
              {{ p.medio }}
              <span v-if="p.fecha" class="rip-cobro__date">{{ formatFechaCobro(p.fecha) }}</span>
            </span>
            <span class="rip-cobro__num">−{{ invoice.currency }} {{ formatNum(p.importe) }}</span>
          </div>

          <div v-if="!invoice.cobro.detalle.length" class="rip-cobro__row rip-cobro__row--paid">
            <span>{{ $t("reservationInvoicePanel.cobro.nothing") }}</span>
            <span class="rip-cobro__num">{{ invoice.currency }} {{ formatNum(0) }}</span>
          </div>

          <div class="rip-cobro__row rip-cobro__row--due"
               :class="{ 'rip-cobro__row--settled': invoice.cobro.pendiente <= 0 }">
            <span>{{ $t("reservationInvoicePanel.cobro.due") }}</span>
            <span class="rip-cobro__num">{{ invoice.currency }} {{ formatNum(invoice.cobro.pendiente) }}</span>
          </div>

          <!-- No hay cobros anotados: la cifra es un supuesto de la forma de pago
               marcada, no un cobro. El PDF sale igual, así que se dice, no se tapa. -->
          <p v-if="invoice.cobro.estimado" class="rip-cobro__warn">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0;margin-top:1px"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="1" fill="currentColor"/></svg>
            {{ $t("reservationInvoicePanel.cobro.assumed") }}
          </p>
        </div>

        <!-- ── Notas ────────────────────────────────────────────────────── -->
        <div v-if="invoice.notes" class="rip-notes">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0;margin-top:1px"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
          {{ invoice.notes }}
        </div>

        <!-- ── Error ─────────────────────────────────────────────────────── -->
        <div v-if="invoice.status === 'error' && invoice.error" class="rip-error-msg">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0;margin-top:1px"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="1" fill="currentColor"/></svg>
          {{ invoice.error }}
        </div>

        <!-- ── Acciones ──────────────────────────────────────────────────── -->
        <div class="rip-card__actions">
          <template v-if="invoice.status === 'draft'">
            <!-- Borrador vacío (sin importe): aviso amable, no el error fiscal de "amount zero" -->
            <div v-if="isEmptyDraft" class="rip-draft-empty">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0;margin-top:1px"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="1" fill="currentColor"/></svg>
              <span v-html="$t('reservationInvoicePanel.draft.emptyHint')"></span>
            </div>
            <div v-else-if="invoice.fiscal_warning" class="rip-fiscal-warn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0;margin-top:1px"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><circle cx="12" cy="17" r="1" fill="currentColor"/></svg>
              <span>{{ invoice.fiscal_warning }}</span>
            </div>
            <div class="rip-draft-note">
              <p class="rip-draft-note__lead" v-html="$t('reservationInvoicePanel.draft.noteLead')"></p>
              <div class="rip-draft-meta" v-if="invoice.next_fiscal_estimate">
                <div class="rip-draft-meta__item">
                  <span class="rip-draft-meta__label">{{ $t("reservationInvoicePanel.draft.nextSequential") }}</span>
                  <span class="rip-draft-meta__value">
                    {{ invoice.next_fiscal_estimate }}
                    <em class="rip-draft-meta__tag">{{ $t("reservationInvoicePanel.draft.estimated") }}</em>
                  </span>
                </div>
              </div>
              <p v-if="invoice.next_fiscal_estimate" class="rip-draft-note__foot">
                {{ $t("reservationInvoicePanel.draft.noteFoot") }}
              </p>
            </div>
            <button class="rip-btn rip-btn--emit" @click="emitModal.open = true" :disabled="emitting || isEmptyDraft">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3"><path d="M20 6L9 17l-5-5"/></svg>
              {{ $t("reservationInvoicePanel.draft.confirmEmit") }}
            </button>
            <button class="rip-btn rip-btn--ghost" @click="previewProforma" :disabled="previewing">
              <span v-if="previewing" class="rip-spinner rip-spinner--sm"></span>
              <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="10" y1="13" x2="14" y2="13"/><line x1="10" y1="17" x2="14" y2="17"/></svg>
              {{ $t("reservationInvoicePanel.draft.previewPdf") }}
            </button>
            <!-- Mandarle el presupuesto al cliente. Antes había que bajar el PDF y
                 adjuntarlo a mano al correo. Solo sale en borradores: de una factura
                 ya emitida no se manda un presupuesto, se manda la factura. -->
            <button class="rip-btn rip-btn--ghost" @click="enviarPresupuesto" :disabled="enviandoPresupuesto">
              <span v-if="enviandoPresupuesto" class="rip-spinner rip-spinner--sm"></span>
              <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
              {{ $t("reservationInvoicePanel.draft.sendQuote") }}
            </button>
            <button class="rip-btn rip-btn--ghost" @click="openEditModal">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              {{ $t("reservationInvoicePanel.draft.edit") }}
            </button>
            <button class="rip-btn rip-btn--danger-ghost" @click="openDeleteModal">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
              {{ $t("reservationInvoicePanel.draft.discard") }}
            </button>
          </template>
          <template v-if="invoice.status === 'sent'">
            <button v-if="canInvoice" class="rip-btn rip-btn--whatsapp" @click="openWhatsappConfirmModal" :disabled="generatingLink">
              <span v-if="generatingLink" class="rip-spinner rip-spinner--sm rip-spinner--white"></span>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              {{ $t("reservationInvoicePanel.actions.sendWhatsapp") }}
            </button>
            <button v-if="!invoice.is_correction" class="rip-btn rip-btn--ghost" @click="openCorrectionModal">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              {{ $t("reservationInvoicePanel.actions.correct") }}
            </button>
            <button v-if="!invoice.is_correction" class="rip-btn rip-btn--danger-ghost" @click="openVoidModal">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
              {{ $t("reservationInvoicePanel.actions.void") }}
            </button>
          </template>
          <template v-else-if="invoice.status === 'processing' || invoice.status === 'pending'">
            <button class="rip-btn rip-btn--ghost" @click="syncInvoice" :disabled="syncing">
              <span v-if="syncing" class="rip-spinner rip-spinner--sm"></span>
              <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
              {{ $t("reservationInvoicePanel.actions.refresh") }}
            </button>
          </template>
          <template v-else-if="invoice.status === 'error'">
            <button class="rip-btn rip-btn--ghost" @click="retryModal.open = true" :disabled="retrying">
              <span v-if="retrying" class="rip-spinner rip-spinner--sm"></span>
              <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
              {{ $t("reservationInvoicePanel.actions.retry") }}
            </button>
            <button class="rip-btn rip-btn--ghost" @click="openEditModal">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              {{ $t("reservationInvoicePanel.actions.editData") }}
            </button>
            <button class="rip-btn rip-btn--danger-ghost" @click="openDeleteModal">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
              {{ $t("reservationInvoicePanel.actions.delete") }}
            </button>
          </template>
        </div>

      </div>

    </template>

    <!-- ══ MODAL: Crear factura — usa ManualInvoiceCreateModal ═════════════ -->
    <manual-invoice-create-modal
      ref="manualModal"
      :reservation-data="reservationData"
      :billing-profile="billingProfile"
      :open-pdf-on-create="false"
      @created="onInvoiceCreated"
    />

    <transition name="rip-fade">
      <div
        v-if="whatsappModal.open"
        class="rip-overlay"
        @click.self="!whatsappModal.sending && (whatsappModal.open = false)"
      >
        <div class="rip-modal rip-modal--sm">
          <div class="rip-modal__head">
            <h5 class="rip-modal__title">{{ $t("reservationInvoicePanel.wa.title") }}</h5>
            <button
              class="rip-modal__x"
              @click="whatsappModal.open = false"
              :disabled="whatsappModal.sending"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="rip-modal__body">
            <p class="rip-modal__desc" v-html="$t('reservationInvoicePanel.wa.desc', { code: whatsappModal.target?.display_code || whatsappModal.target?.sequence_number || $t('reservationInvoicePanel.wa.issuedFallback') })"></p>
            <div class="rip-checklist">
              <div class="rip-checklist__item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                {{ $t("reservationInvoicePanel.wa.checkPhone") }}
              </div>
              <div class="rip-checklist__item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                {{ $t("reservationInvoicePanel.wa.checkPdf") }}
              </div>
            </div>
          </div>
          <div class="rip-modal__foot">
            <button
              class="rip-btn rip-btn--ghost"
              @click="whatsappModal.open = false"
              :disabled="whatsappModal.sending"
            >
              {{ $t("reservationInvoicePanel.common.cancel") }}
            </button>
            <button
              class="rip-btn rip-btn--whatsapp"
              @click="confirmSendInvoiceWhatsapp"
              :disabled="whatsappModal.sending"
            >
              <span v-if="whatsappModal.sending" class="rip-spinner rip-spinner--sm rip-spinner--white"></span>
              <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
              {{ whatsappModal.sending ? $t("reservationInvoicePanel.common.sending") : $t("reservationInvoicePanel.wa.send") }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: Confirmar enviar link de facturación al huésped ═════════════ -->
    <transition name="rip-fade">
      <div
        v-if="sendLinkModal.open"
        class="rip-overlay"
        @click.self="!sendLinkModal.sending && (sendLinkModal.open = false)"
      >
        <div class="rip-modal rip-modal--sm">
          <div class="rip-modal__head">
            <h5 class="rip-modal__title">{{ $t("reservationInvoicePanel.sendLink.title") }}</h5>
            <button class="rip-modal__x" @click="sendLinkModal.open = false" :disabled="sendLinkModal.sending">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="rip-modal__body">
            <p class="rip-modal__desc" v-html="$t('reservationInvoicePanel.sendLink.desc')"></p>
            <div class="rip-checklist">
              <div class="rip-checklist__item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                {{ $t("reservationInvoicePanel.wa.checkPhone") }}
              </div>
              <div class="rip-checklist__item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                {{ $t("reservationInvoicePanel.sendLink.checkNoInvoice") }}
              </div>
            </div>
          </div>
          <div class="rip-modal__foot">
            <button class="rip-btn rip-btn--ghost" @click="sendLinkModal.open = false" :disabled="sendLinkModal.sending">{{ $t("reservationInvoicePanel.common.cancel") }}</button>
            <button class="rip-btn rip-btn--brand" @click="confirmSendLink" :disabled="sendLinkModal.sending">
              <span v-if="sendLinkModal.sending" class="rip-spinner rip-spinner--sm rip-spinner--white"></span>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              {{ sendLinkModal.sending ? $t("reservationInvoicePanel.common.sending") : $t("reservationInvoicePanel.sendLink.button") }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: Confirmar reintento de emisión ══════════════════════════════ -->
    <transition name="rip-fade">
      <div
        v-if="retryModal.open"
        class="rip-overlay"
        @click.self="!retrying && (retryModal.open = false)"
      >
        <div class="rip-modal rip-modal--sm">
          <div class="rip-modal__head">
            <h5 class="rip-modal__title">{{ $t("reservationInvoicePanel.retryModal.title") }}</h5>
            <button class="rip-modal__x" @click="retryModal.open = false" :disabled="retrying">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="rip-modal__body">
            <p class="rip-modal__desc" v-html="$t('reservationInvoicePanel.retryModal.desc')"></p>
            <div class="rip-checklist">
              <div class="rip-checklist__item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                {{ $t("reservationInvoicePanel.retryModal.check1") }}
              </div>
              <div class="rip-checklist__item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                {{ $t("reservationInvoicePanel.retryModal.check2") }}
              </div>
            </div>
          </div>
          <div class="rip-modal__foot">
            <button class="rip-btn rip-btn--ghost" @click="retryModal.open = false" :disabled="retrying">{{ $t("reservationInvoicePanel.common.cancel") }}</button>
            <button class="rip-btn rip-btn--brand" @click="confirmRetry" :disabled="retrying">
              <span v-if="retrying" class="rip-spinner rip-spinner--sm rip-spinner--white"></span>
              <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
              {{ retrying ? $t("reservationInvoicePanel.common.retrying") : $t("reservationInvoicePanel.retryModal.title") }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: Anular / Abono (Nota de crédito) ════════════════════════════ -->
    <transition name="rip-fade">
      <div v-if="voidModal.open" class="rip-overlay" @click.self="!voidModal.voiding && (voidModal.open = false)">
        <div class="rip-modal">
          <template v-if="voidModal.done">
            <div class="rip-modal__head">
              <h5 class="rip-modal__title">{{ $t("reservationInvoicePanel.voidModal.doneTitle") }}</h5>
              <button class="rip-modal__x" @click="voidModal.open = false"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </div>
            <div class="rip-modal__body">
              <div class="rip-checklist">
                <div class="rip-checklist__item"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg><span v-html="$t('reservationInvoicePanel.voidModal.creditNoteIssued', { code: voidModal.creditNoteSeries })"></span></div>
                <div class="rip-checklist__item"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>{{ $t("reservationInvoicePanel.voidModal.chargesReleased") }}</div>
                <div class="rip-checklist__item"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>{{ $t("reservationInvoicePanel.voidModal.clientCanRequest") }}</div>
              </div>
              <p class="rip-modal__hint">{{ $t("reservationInvoicePanel.voidModal.doneHint") }}</p>
            </div>
            <div class="rip-modal__foot">
              <button class="rip-btn rip-btn--brand" @click="voidModal.open = false">Entendido</button>
            </div>
          </template>
          <template v-else>
            <div class="rip-modal__head">
              <h5 class="rip-modal__title">{{ $t("reservationInvoicePanel.voidModal.formTitle") }}</h5>
              <button class="rip-modal__x" @click="voidModal.open = false" :disabled="voidModal.voiding"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </div>
            <div class="rip-modal__body">
              <p class="rip-modal__desc" v-html="$t('reservationInvoicePanel.voidModal.formDesc')"></p>
              <div class="rip-checklist">
                <div class="rip-checklist__item"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>{{ $t("reservationInvoicePanel.voidModal.stepAnnul", { code: (voidModal.target && voidModal.target.display_code) || (invoice && invoice.display_code) || '' }) }}</div>
                <div class="rip-checklist__item"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>{{ $t("reservationInvoicePanel.voidModal.stepFree") }}</div>
                <div class="rip-checklist__item"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>{{ $t("reservationInvoicePanel.voidModal.stepNewNumber") }}</div>
              </div>
              <div class="rip-notice">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="1" fill="currentColor"/></svg>
                <span v-html="$t('reservationInvoicePanel.voidModal.notice')"></span>
              </div>
            </div>
            <div class="rip-modal__foot">
              <button class="rip-btn rip-btn--ghost" @click="voidModal.open = false" :disabled="voidModal.voiding">{{ $t("reservationInvoicePanel.common.cancel") }}</button>
              <button class="rip-btn rip-btn--danger" @click="confirmVoid" :disabled="voidModal.voiding">
                <span v-if="voidModal.voiding" class="rip-spinner rip-spinner--sm rip-spinner--white"></span>
                <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                {{ voidModal.voiding ? $t("reservationInvoicePanel.voidModal.voiding") : $t("reservationInvoicePanel.voidModal.confirm") }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: Editar datos del cliente ════════════════════════════════════ -->
    <transition name="rip-fade">
      <div v-if="editModal.open" ref="editOverlay" class="rip-overlay" @click.self="editModal.open = false">
        <div class="rip-modal rip-modal--lg">
          <div class="rip-modal__head">
            <h5 class="rip-modal__title">{{ isDraftEdit ? $t("reservationInvoicePanel.edit.titleDraft") : $t("reservationInvoicePanel.edit.titleClient") }}</h5>
            <button class="rip-modal__x" @click="editModal.open = false"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
          </div>
          <div class="rip-modal__body">
            <p class="rip-modal__desc">{{ isDraftEdit ? $t("reservationInvoicePanel.edit.descDraft") : $t("reservationInvoicePanel.edit.descSent") }}</p>
            <div v-if="isDraftEdit && invoice && invoice.fiscal_warning" class="rip-fiscal-warn" style="margin:0 0 4px">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0;margin-top:1px"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><circle cx="12" cy="17" r="1" fill="currentColor"/></svg>
              <span>{{ invoice.fiscal_warning }}</span>
            </div>
            <div class="rip-form">
              <!-- Tipo: decide si el NIF es obligatorio (empresa) al emitir -->
              <div class="rip-field rip-field--full">
                <label class="rip-label">{{ $t("reservationInvoicePanel.edit.type") }}</label>
                <div class="rip-typetoggle">
                  <button type="button" class="rip-typebtn" :class="{ 'is-active': editModal.form.customer_type !== 'company' }" @click="editModal.form.customer_type = 'individual'">
                    <b>{{ $t("reservationInvoicePanel.edit.individual") }}</b><small>{{ $t("reservationInvoicePanel.edit.individualSub") }}</small>
                  </button>
                  <button type="button" class="rip-typebtn" :class="{ 'is-active': editModal.form.customer_type === 'company' }" @click="editModal.form.customer_type = 'company'">
                    <b>{{ $t("reservationInvoicePanel.edit.company") }}</b><small>{{ $t("reservationInvoicePanel.edit.companySub") }}</small>
                  </button>
                </div>
              </div>
              <div class="rip-field rip-field--full"><label class="rip-label">{{ editModal.form.customer_type === 'company' ? $t("reservationInvoicePanel.edit.legalName") : $t("reservationInvoicePanel.edit.fullName") }} *</label><input v-model="editModal.form.customer_name" type="text" class="rip-input" :placeholder="editModal.form.customer_type === 'company' ? $t('reservationInvoicePanel.edit.legalNamePlaceholder') : $t('reservationInvoicePanel.edit.fullNamePlaceholder')" /></div>
              <div class="rip-field"><label class="rip-label">{{ $t("reservationInvoicePanel.edit.country") }} <span class="rip-opt">{{ $t("reservationInvoicePanel.common.optional") }}</span></label>
                <div class="rip-countrywrap"><CountrySelect v-model="editModal.form.customer_country" :placeholder="$t('reservationInvoicePanel.edit.countryPlaceholder')" /></div>
              </div>
              <div class="rip-field"><label class="rip-label">{{ editTaxLabel }} <span class="rip-opt">{{ editModal.form.customer_type === 'company' ? $t("reservationInvoicePanel.edit.taxRequiredOnEmit") : $t("reservationInvoicePanel.common.optional") }}</span></label><input v-model="editModal.form.customer_tax_id" type="text" class="rip-input" :placeholder="editModal.form.customer_type === 'company' ? $t('reservationInvoicePanel.edit.taxIdPlaceholderCompany') : $t('reservationInvoicePanel.edit.taxIdPlaceholderIndividual')" /></div>
              <div class="rip-field rip-field--full"><label class="rip-label">{{ $t("reservationInvoicePanel.edit.email") }} <span class="rip-opt">{{ $t("reservationInvoicePanel.common.optional") }}</span></label><input v-model="editModal.form.customer_email" type="email" class="rip-input" :placeholder="$t('reservationInvoicePanel.edit.emailPlaceholder')" /></div>
              <div class="rip-field rip-field--full"><label class="rip-label">{{ $t("reservationInvoicePanel.edit.phone") }} <span class="rip-opt">{{ $t("reservationInvoicePanel.common.optional") }}</span></label><input v-model="editModal.form.customer_phone" type="tel" class="rip-input" :placeholder="$t('reservationInvoicePanel.edit.phonePlaceholder')" /></div>
              <div class="rip-field rip-field--full"><label class="rip-label">{{ $t("reservationInvoicePanel.edit.fiscalAddress") }} <span class="rip-opt">{{ $t("reservationInvoicePanel.common.optional") }}</span></label><input v-model="editModal.form.customer_address" type="text" class="rip-input" :placeholder="$t('reservationInvoicePanel.edit.addressPlaceholder')" /></div>
              <div class="rip-field"><label class="rip-label">{{ $t("reservationInvoicePanel.edit.city") }} <span class="rip-opt">{{ $t("reservationInvoicePanel.common.optional") }}</span></label><input v-model="editModal.form.customer_city" type="text" class="rip-input" /></div>
              <div class="rip-field"><label class="rip-label">{{ $t("reservationInvoicePanel.edit.region") }} <span class="rip-opt">{{ $t("reservationInvoicePanel.common.optional") }}</span></label><input v-model="editModal.form.customer_state" type="text" class="rip-input" /></div>
              <div class="rip-field"><label class="rip-label">{{ $t("reservationInvoicePanel.edit.zip") }} <span class="rip-opt">{{ $t("reservationInvoicePanel.common.optional") }}</span></label><input v-model="editModal.form.customer_zip" type="text" class="rip-input" /></div>
            </div>
          </div>
          <div class="rip-modal__foot">
            <button class="rip-btn rip-btn--ghost" @click="editModal.open = false" :disabled="editModal.saving">{{ $t("reservationInvoicePanel.common.cancel") }}</button>
            <button class="rip-btn rip-btn--brand" @click="saveAndRetry" :disabled="editModal.saving">
              <span v-if="editModal.saving" class="rip-spinner rip-spinner--sm rip-spinner--white"></span>
              <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              {{ editModal.saving ? $t("reservationInvoicePanel.common.saving") : (isDraftEdit ? $t("reservationInvoicePanel.edit.saveChanges") : $t("reservationInvoicePanel.edit.saveAndRetry")) }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: Eliminar factura ═════════════════════════════════════════ -->
    <transition name="rip-fade">
      <div v-if="deleteModal.open" class="rip-overlay" @click.self="deleteModal.open = false">
        <div class="rip-modal rip-modal--sm">
          <div class="rip-modal__head">
            <h5 class="rip-modal__title">{{ $t("reservationInvoicePanel.deleteModal.title") }}</h5>
            <button class="rip-modal__x" @click="deleteModal.open = false"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
          </div>
          <div class="rip-modal__body">
            <p class="rip-modal__desc">{{ $t("reservationInvoicePanel.deleteModal.desc") }}</p>
            <div class="rip-notice">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="1" fill="currentColor"/></svg>
              {{ $t("reservationInvoicePanel.deleteModal.noticeIrreversible") }}
            </div>
          </div>
          <div class="rip-modal__foot">
            <button class="rip-btn rip-btn--ghost" @click="deleteModal.open = false" :disabled="deleteModal.deleting">{{ $t("reservationInvoicePanel.common.cancel") }}</button>
            <button class="rip-btn rip-btn--danger" @click="confirmDelete" :disabled="deleteModal.deleting">
              <span v-if="deleteModal.deleting" class="rip-spinner rip-spinner--sm rip-spinner--white"></span>
              <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
              {{ deleteModal.deleting ? $t("reservationInvoicePanel.common.deleting") : $t("reservationInvoicePanel.actions.delete") }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: Confirmar y emitir factura ═══════════════════════════════ -->
    <transition name="rip-fade">
      <div v-if="emitModal.open" class="rip-overlay" @click.self="!emitting && (emitModal.open = false)">
        <div class="rip-modal rip-modal--sm">
          <div class="rip-modal__head">
            <div class="rip-modal__icon rip-modal__icon--emit">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <div>
              <h5 class="rip-modal__title">{{ $t("reservationInvoicePanel.emit.title") }}</h5>
              <p class="rip-modal__sub">{{ $t("reservationInvoicePanel.emit.subtitle") }}</p>
            </div>
            <button class="rip-modal__x" @click="emitModal.open = false" :disabled="emitting"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
          </div>
          <div class="rip-modal__body">
            <p class="rip-modal__desc">
              <i18n path="reservationInvoicePanel.emit.desc" tag="span">
                <template #name><strong>{{ invoice && invoice.customer_name }}</strong></template>
              </i18n>
            </p>
            <ul class="rip-emit-steps">
              <li>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                <span v-if="invoice && invoice.next_fiscal_estimate" v-html="$t('reservationInvoicePanel.emit.stepNumberEstimate', { code: invoice.next_fiscal_estimate })"></span>
                <span v-else v-html="$t('reservationInvoicePanel.emit.stepNumberFinal')"></span>
              </li>
              <li>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                {{ $t("reservationInvoicePanel.emit.stepPdf") }}
              </li>
            </ul>
            <div class="rip-emit-warn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0;margin-top:1px"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><circle cx="12" cy="17" r="1" fill="currentColor"/></svg>
              <span v-html="$t('reservationInvoicePanel.emit.warn')"></span>
            </div>
          </div>
          <div class="rip-modal__foot">
            <button class="rip-btn rip-btn--ghost" @click="emitModal.open = false" :disabled="emitting">{{ $t("reservationInvoicePanel.common.cancel") }}</button>
            <button class="rip-btn rip-btn--emit" @click="confirmEmit" :disabled="emitting">
              <span v-if="emitting" class="rip-spinner rip-spinner--sm rip-spinner--white"></span>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3"><path d="M20 6L9 17l-5-5"/></svg>
              {{ emitting ? $t("reservationInvoicePanel.common.emitting") : $t("reservationInvoicePanel.emit.confirm") }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: Confirmar crear OTRA factura para la reserva ═════════════ -->
    <transition name="rip-fade">
      <div v-if="newInvoiceModal.open" class="rip-overlay" @click.self="!creatingDraft && (newInvoiceModal.open = false)">
        <div class="rip-modal rip-modal--sm">
          <div class="rip-modal__head">
            <div class="rip-modal__icon">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
            </div>
            <div>
              <h5 class="rip-modal__title">{{ $t("reservationInvoicePanel.newInvoice.title") }}</h5>
              <p class="rip-modal__sub">{{ $t("reservationInvoicePanel.newInvoice.subtitle") }}</p>
            </div>
            <button class="rip-modal__x" @click="newInvoiceModal.open = false" :disabled="creatingDraft"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
          </div>
          <div class="rip-modal__body">
            <p class="rip-modal__desc" v-html="$t('reservationInvoicePanel.newInvoice.desc')"></p>
            <ul class="rip-emit-steps">
              <li>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                <span v-html="$t('reservationInvoicePanel.newInvoice.step1')"></span>
              </li>
              <li>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                {{ $t("reservationInvoicePanel.newInvoice.step2") }}
              </li>
            </ul>

            <!-- Reservas de varias habitaciones: entera o por habitación.
                 Solo aparece cuando hay más de una; con una sola no significa nada. -->
            <div v-if="billableRooms.splittable" class="rip-scope">
              <span class="rip-scope__label">{{ $t("reservationInvoicePanel.newInvoice.scopeTitle") }}</span>

              <button
                type="button"
                class="rip-scope__opt"
                :class="{ 'is-on': !scope.roomId }"
                @click="scope.roomId = null"
              >
                <span class="rip-scope__name">{{ $t("reservationInvoicePanel.newInvoice.whole") }}</span>
                <span class="rip-scope__hint">
                  {{ $t("reservationInvoicePanel.newInvoice.wholeHint", { n: billableRooms.rooms.length }) }}
                </span>
              </button>

              <button
                type="button"
                class="rip-scope__opt"
                :class="{ 'is-on': !!scope.roomId }"
                @click="scope.roomId = scope.roomId || billableRooms.rooms[0]?.id || null"
              >
                <span class="rip-scope__name">{{ $t("reservationInvoicePanel.newInvoice.perRoom") }}</span>
                <span class="rip-scope__hint">{{ $t("reservationInvoicePanel.newInvoice.perRoomHint") }}</span>
              </button>

              <div v-if="scope.roomId" class="rip-scope__rooms">
                <span class="rip-scope__label rip-scope__label--sub">
                  {{ $t("reservationInvoicePanel.newInvoice.pickRoom") }}
                </span>
                <button
                  v-for="room in billableRooms.rooms"
                  :key="room.id"
                  type="button"
                  class="rip-scope__room"
                  :class="{ 'is-on': scope.roomId === room.id }"
                  @click="scope.roomId = room.id"
                >
                  <span class="rip-scope__roomName">{{ room.label }}</span>
                  <span class="rip-scope__roomAmount">
                    {{ $t("reservationInvoicePanel.newInvoice.roomLodging") }}
                    {{ billableRooms.currency }} {{ formatNum(room.lodging) }}
                  </span>
                </button>
              </div>
            </div>
          </div>
          <div class="rip-modal__foot">
            <button class="rip-btn rip-btn--ghost" @click="newInvoiceModal.open = false" :disabled="creatingDraft">{{ $t("reservationInvoicePanel.common.cancel") }}</button>
            <button class="rip-btn rip-btn--brand" @click="confirmNewInvoice" :disabled="creatingDraft">
              <span v-if="creatingDraft" class="rip-spinner rip-spinner--sm rip-spinner--white"></span>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              {{ creatingDraft ? $t("reservationInvoicePanel.common.creating") : $t("reservationInvoicePanel.newInvoice.createDraft") }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: Corregir factura (rectificativa) ═════════════════════════ -->
    <transition name="rip-fade">
      <div
        v-if="correctionModal.open"
        class="cm-overlay"
        @click.self="!correctionModal.saving && (correctionModal.open = false)"
      >
        <div class="cm-container" :class="correctionModal.done ? 'cm-container--sm' : ''">

          <!-- ── ÉXITO ─────────────────────────────────────────────────── -->
          <template v-if="correctionModal.done">
            <div class="cm-header">
              <h3 class="cm-h3">
                <span class="cm-h3__icon cm-h3__icon--success">
                  <i class="fas fa-check"></i>
                </span>
                {{ $t("reservationInvoicePanel.correction.doneTitle") }}
              </h3>
              <button class="cm-x" @click="correctionModal.open = false">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="cm-body">
              <div class="cm-success-code">
                {{ (correctionModal.corrective && correctionModal.corrective.display_code) || '—' }}
              </div>
              <div class="cm-checklist">
                <div class="cm-checklist__item">
                  <span class="cm-check-dot"></span>
                  <span v-html="$t('reservationInvoicePanel.correction.createdOk', { code: (correctionModal.corrective && correctionModal.corrective.display_code) || '' })"></span>
                </div>
                <div class="cm-checklist__item">
                  <span class="cm-check-dot"></span>
                  {{ $t("reservationInvoicePanel.correction.originalKept") }}
                </div>
                <div class="cm-checklist__item">
                  <span class="cm-check-dot"></span>
                  {{ $t("reservationInvoicePanel.correction.pdfSoon") }}
                </div>
              </div>
            </div>
            <div class="cm-footer">
              <button class="cm-btn cm-btn--save" @click="correctionModal.open = false">
                <i class="fas fa-check"></i>
                {{ $t("reservationInvoicePanel.common.understood") }}
              </button>
            </div>
          </template>

          <!-- ── FORMULARIO ────────────────────────────────────────────── -->
          <template v-else>
            <div class="cm-header">
              <h3 class="cm-h3">
                <span class="cm-h3__icon">
                  <i class="fas fa-pen-to-square"></i>
                </span>
                {{ $t("reservationInvoicePanel.correction.title") }}
                <span class="cm-h3__code">{{ (correctionModal.target && correctionModal.target.display_code) || (invoice && invoice.display_code) }}</span>
              </h3>
              <button class="cm-x" @click="correctionModal.open = false" :disabled="correctionModal.saving">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <div class="cm-body">

              <!-- Info box -->
              <div class="cm-infobox">
                <i class="fas fa-circle-info cm-infobox__icon"></i>
                <p class="cm-infobox__text" v-html="$t('reservationInvoicePanel.correction.infobox', { code: (invoice && invoice.display_code) || '' })"></p>
              </div>

              <!-- Motivo -->
              <div class="cm-field cm-field--full">
                <label class="cm-label">
                  {{ $t("reservationInvoicePanel.correction.reasonLabel") }}
                  <span class="cm-label__req">*</span>
                </label>
                <select v-model="correctionModal.form.correction_reason" class="cm-input cm-input--select">
                  <option value="">{{ $t("reservationInvoicePanel.correction.selectReason") }}</option>
                  <option value="NIF / CIF o Tax ID incorrecto">{{ $t("reservationInvoicePanel.correction.reasonTaxId") }}</option>
                  <option value="Nombre o razón social incorrecto">{{ $t("reservationInvoicePanel.correction.reasonName") }}</option>
                  <option value="Dirección fiscal incorrecta">{{ $t("reservationInvoicePanel.correction.reasonAddress") }}</option>
                  <option value="Email incorrecto">{{ $t("reservationInvoicePanel.correction.reasonEmail") }}</option>
                  <option value="Corrección general de datos fiscales">{{ $t("reservationInvoicePanel.correction.reasonGeneral") }}</option>
                  <option value="__custom__">{{ $t("reservationInvoicePanel.correction.reasonOther") }}</option>
                </select>
                <input
                  v-if="correctionModal.form.correction_reason === '__custom__'"
                  v-model="correctionModal.customReason"
                  type="text"
                  class="cm-input"
                  style="margin-top:6px"
                  :placeholder="$t('reservationInvoicePanel.correction.customReasonPlaceholder')"
                />
              </div>

              <!-- Divider -->
              <div class="cm-divider">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                {{ $t("reservationInvoicePanel.correction.recipientDivider") }}
              </div>

              <!-- Form grid -->
              <div class="cm-grid">
                <div class="cm-field cm-field--full">
                  <label class="cm-label">{{ $t("reservationInvoicePanel.correction.nameOrLegalName") }}</label>
                  <input v-model="correctionModal.form.customer_name" type="text" class="cm-input" />
                </div>
                <div class="cm-field">
                  <label class="cm-label">{{ $t("reservationInvoicePanel.correction.taxIdFull") }}</label>
                  <input v-model="correctionModal.form.customer_tax_id" type="text" class="cm-input" placeholder="B12345678" />
                </div>
                <div class="cm-field">
                  <label class="cm-label">
                    {{ $t("reservationInvoicePanel.edit.country") }}
                    <span class="cm-label__opt">{{ $t("reservationInvoicePanel.correction.countryTwoLetters") }}</span>
                  </label>
                  <input v-model="correctionModal.form.customer_country" type="text" class="cm-input" placeholder="ES" maxlength="2" style="text-transform:uppercase" />
                </div>
                <div class="cm-field cm-field--full">
                  <label class="cm-label">
                    {{ $t("reservationInvoicePanel.edit.email") }}
                    <span class="cm-label__opt">{{ $t("reservationInvoicePanel.common.optionalParen") }}</span>
                  </label>
                  <input v-model="correctionModal.form.customer_email" type="email" class="cm-input" />
                </div>
                <div class="cm-field cm-field--full">
                  <label class="cm-label">
                    {{ $t("reservationInvoicePanel.edit.fiscalAddress") }}
                    <span class="cm-label__opt">{{ $t("reservationInvoicePanel.common.optionalParen") }}</span>
                  </label>
                  <input v-model="correctionModal.form.customer_address" type="text" class="cm-input" />
                </div>
                <div class="cm-field">
                  <label class="cm-label">
                    {{ $t("reservationInvoicePanel.edit.city") }}
                    <span class="cm-label__opt">{{ $t("reservationInvoicePanel.common.optionalParen") }}</span>
                  </label>
                  <input v-model="correctionModal.form.customer_city" type="text" class="cm-input" />
                </div>
                <div class="cm-field">
                  <label class="cm-label">
                    {{ $t("reservationInvoicePanel.edit.zip") }}
                    <span class="cm-label__opt">{{ $t("reservationInvoicePanel.common.optionalParen") }}</span>
                  </label>
                  <input v-model="correctionModal.form.customer_zip" type="text" class="cm-input" />
                </div>
              </div>

              <!-- Divider pago -->
              <div class="cm-divider">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
                {{ $t("reservationInvoicePanel.correction.paymentDivider") }}
              </div>

              <div class="cm-field cm-field--full">
                <label class="cm-label">
                  {{ $t("reservationInvoicePanel.correction.paymentLabel") }}
                  <span class="cm-label__opt">{{ $t("reservationInvoicePanel.common.optionalParen") }}</span>
                </label>
                <select v-model="correctionModal.form.payment_method" class="cm-input cm-input--select">
                  <option value="">{{ $t("reservationInvoicePanel.correction.paymentUnspecified") }}</option>
                  <option value="efectivo">{{ $t("reservationInvoicePanel.correction.paymentCash") }}</option>
                  <option value="tarjeta">{{ $t("reservationInvoicePanel.correction.paymentCard") }}</option>
                  <option value="transferencia">{{ $t("reservationInvoicePanel.correction.paymentTransfer") }}</option>
                  <option value="stripe">{{ $t("reservationInvoicePanel.correction.paymentStripe") }}</option>
                  <option value="booking">Booking.com</option>
                  <option value="airbnb">Airbnb</option>
                </select>
              </div>

            </div><!-- /cm-body -->

            <div class="cm-footer">
              <button class="cm-btn cm-btn--cancel" @click="correctionModal.open = false" :disabled="correctionModal.saving">
                {{ $t("reservationInvoicePanel.common.cancel") }}
              </button>
              <button
                class="cm-btn cm-btn--save"
                @click="confirmCorrection"
                :disabled="correctionModal.saving || !correctionModal.form.correction_reason"
              >
                <span v-if="correctionModal.saving" class="cm-spinner"></span>
                <i v-else class="fas fa-check"></i>
                {{ correctionModal.saving ? $t("reservationInvoicePanel.common.emitting") : $t("reservationInvoicePanel.correction.emitRectif") }}
              </button>
            </div>
          </template>

        </div>
      </div>
    </transition>

  </div>
</template>


<script>
import axios from 'axios';
import { $toast } from '@/utils/useToast';
import { buildCountryOptions } from '@/utils/countryOptions';
import ManualInvoiceCreateModal from '@/components/invoices/ManualInvoiceCreateModal.vue';
import CountrySelect from '@/components/invoices/CountrySelect.vue';

export default {
  name: 'ReservationInvoicePanel',

  components: { ManualInvoiceCreateModal, CountrySelect },

  props: {
    reservationData: { type: Object, required: true },
    // Cuando el PADRE muestra el listado "Facturas emitidas" arriba (antes de los
    // cargos), el panel oculta el suyo para no duplicarlo; sigue emitiéndolo por evento.
    hideEmittedList: { type: Boolean, default: false },
    /* Líneas del documento EN CURSO (noches, extras, descuentos, cargos), que las
       calcula el padre con `conceptsFor(inv, { asDraft: true })`. Sin esto el
       borrador solo enseñaba los datos del cliente: no se podía revisar QUÉ se
       factura antes de emitir, y emitir quema número. */
    draftConcepts: { type: Array, default: () => [] },
  },

  data() {
    return {
      loading:        true,
      invoice:        null,        // factura en curso (borrador / emitiéndose / error)
      emittedInvoices: [],         // facturas ya asentadas (emitidas / anuladas / corregidas)
      generatingLink: false,
      linkAction:     null,
      syncing:        false,
      retrying:       false,
      autoSyncTimer:  null,
      autoSyncTicks:  0,
      whatsappModal:  { open: false, sending: false },

      // Modal: Confirmar enviar link de facturación al huésped (dispara un WhatsApp real)
      sendLinkModal:  { open: false, sending: false },

      // Modal: Confirmar reintento de emisión
      retryModal:     { open: false },

      // Modal: Anular / Abono
      voidModal:   { open: false, voiding: false, done: false, creditNoteSeries: null },

      // Modal: Editar datos del cliente
      editModal:   { open: false, saving: false, form: {} },

      // Modal: Eliminar
      deleteModal: { open: false, deleting: false },

      // Modal: Confirmar y emitir factura
      emitModal:   { open: false },

      // Modal: Confirmar crear OTRA factura para la reserva (cargo posterior)
      newInvoiceModal: { open: false },

      /* Facturar toda la reserva o una habitación. `roomId` nulo = la de siempre.
         Se queda vacío hasta que el servidor dice qué habitaciones hay. */
      scope: { roomId: null },
      billableRooms: { splittable: false, currency: 'EUR', rooms: [] },

      // Modal: Corregir factura (rectificativa)
      correctionModal: {
        open:         false,
        saving:       false,
        done:         false,
        corrective:   null,   // Invoice devuelta tras éxito
        customReason: '',     // Campo libre cuando el select = '__custom__'
        form: {
          customer_name:     '',
          customer_tax_id:   '',
          customer_country:  '',
          customer_email:    '',
          customer_address:  '',
          customer_city:     '',
          customer_state:    '',
          customer_zip:      '',
          correction_reason: '',
          payment_method:    '',
        },
      },

      // Facturas anteriores de esta reserva (originales corregidas, etc.)
      previousInvoices: [],

      // Borrador → emitir
      creatingDraft: false,
      emitting: false,
      previewing: false,
      enviandoPresupuesto: false,
    };
  },

  computed: {
    reservaId() { return this.reservationData?.id; },

    // ¿Estamos editando un BORRADOR? (afina el copy/botón del modal de edición)
    isDraftEdit() { return this.invoice?.status === 'draft'; },

    // Borrador SIN importe (p.ej. una factura adicional recién creada, sin cargos aún):
    // no se puede emitir todavía. Mostramos un aviso amable en vez del error fiscal.
    isEmptyDraft() {
      return this.invoice?.status === 'draft' && !(Number(this.invoice?.total) > 0);
    },

    // Fecha PREVISTA de emisión = día del checkout (la definitiva se fija al emitir).
    plannedEmissionDate() {
      const r = this.reservationData || {};
      const raw = r.fecha_salida || r.check_out || r.checkout || r.salida || r.fecha_checkout || r.end_date;
      if (!raw) return null;
      const d = new Date(String(raw).replace(' ', 'T'));
      if (isNaN(d.getTime())) return null;
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    // Etiqueta del documento fiscal según el país elegido (cambia en vivo).
    editTaxLabel() {
      const c = (this.editModal.form.customer_country || 'ES').toUpperCase();
      const map = {
        ES: 'DNI / NIE / NIF / CIF', PT: 'NIF', FR: 'SIREN / TVA', IT: 'Partita IVA / CF',
        DE: 'USt-IdNr.', GB: 'VAT / UTR', IE: 'PPS / VAT', NL: 'BTW-nummer',
        BE: 'BTW / TVA', PT_: 'NIF', CO: 'Cédula / NIT', MX: 'RFC', AR: 'CUIT / CUIL',
        BR: 'CPF / CNPJ', CL: 'RUT', PE: 'RUC / DNI', UY: 'RUT / CI', EC: 'RUC / Cédula',
        US: 'EIN / SSN', CA: 'BN / SIN',
      };
      return map[c] || 'NIF / Tax ID';
    },

    // Selector fiscal: MISMA lista COMPLETA (todos los países ISO) que usa el modal
    // de crear factura, para no quedarnos cortos al editar.
    billingCountries() {
      return buildCountryOptions('es');
    },

    // Solicitud de factura del HUÉSPED (pedida en el motor/chat) aún sin emitir.
    invoiceRequest() {
      const r = this.reservationData?.invoice_request;
      return r && !this.invoice ? r : null;
    },
    // Datos fiscales que el huésped adjuntó (payload de la solicitud) → filas legibles.
    requestFiscal() {
      const p = this.invoiceRequest?.payload;
      if (!p || typeof p !== 'object') return [];
      const typeLabel = { company: this.$t('reservationInvoicePanel.request.typeCompany'), individual: this.$t('reservationInvoicePanel.request.typeIndividual') };
      const out = [];
      if (p.type) out.push({ label: this.$t('reservationInvoicePanel.request.type'), value: typeLabel[p.type] || p.type });
      [
        ['legal_name',    this.$t('reservationInvoicePanel.request.legalName')],
        ['tax_id',        this.$t('reservationInvoicePanel.request.taxId')],
        ['address_line1', this.$t('reservationInvoicePanel.request.address')],
        ['postal_code',   this.$t('reservationInvoicePanel.request.postalCode')],
        ['city',          this.$t('reservationInvoicePanel.request.city')],
        ['state',         this.$t('reservationInvoicePanel.request.state')],
        ['country_code',  this.$t('reservationInvoicePanel.request.country')],
        ['email',         this.$t('reservationInvoicePanel.request.email')],
      ].forEach(([k, label]) => {
        const v = p[k];
        if (v !== null && v !== undefined && String(v).trim() !== '') {
          out.push({ label, value: String(v) });
        }
      });
      return out;
    },

    billingProfile() {
      // Multi-emisor: emisor resuelto para la propiedad de la reserva.
      return this.reservationData?.billing_emisor
          || this.$store.state.userRoot?.billing_profile
          || this.$store.state.userRoot?.billingProfile
          || null;
    },

    canInvoice() {
      const bp = this.reservationData?.billing_emisor || this.$store.state.userRoot?.billing_profile || this.$store.state.userRoot?.billingProfile;
      if (!bp || bp.is_active === false) return false;
      // El email del emisor es opcional para facturar (no se exige).
      const required = ['country','legal_name','tax_id','address_street','address_city','address_region','address_zip'];
      const allFilled = required.every(k => {
        const v = bp[k]; return v !== null && v !== undefined && String(v).trim() !== '';
      });
      return allFilled && this.$store.state.userRoot?.servicios?.electronic_invoicing === true;
    },

    statusClass() { return this.statusClassFor(this.invoice); },
    statusLabel() { return this.statusLabelFor(this.invoice); },

    // Suma de las líneas que se enseñan. No es el total del documento (ese lo da el
    // servidor): sirve para CONTRASTARLO, no para sustituirlo.
    conceptsTotal() {
      return parseFloat(
        this.draftConcepts
          .reduce((a, c) => a + (parseFloat(c.total_with_vat) || 0), 0)
          .toFixed(2),
      );
    },

    /* ¿Las líneas no suman el total de la tarjeta? Entonces se dice, no se disimula:
       un documento cuyo desglose no cuadra con su total no se debe emitir a ciegas.
       (Caso conocido: un borrador de UNA habitación junto a un desglose de toda la
       reserva.) */
    conceptsMismatch() {
      if (!this.draftConcepts.length) return false;
      const total = parseFloat(this.invoice?.total);
      if (!Number.isFinite(total)) return false;
      return Math.abs(total - this.conceptsTotal) > 0.01;
    },
  },

  watch: {
    reservaId: { immediate: true, handler(v) { if (v) this.loadInvoice(); } },
  },

  beforeDestroy() {
    this.stopAutoSyncPolling();
    // Si el overlay quedó teletransportado a <body>, retíralo al destruir el panel.
    if (this._editOverlayEl && this._editOverlayEl.parentNode) {
      this._editOverlayEl.parentNode.removeChild(this._editOverlayEl);
    }
  },

  methods: {
    // ─── Estado ──────────────────────────────────────────────────────────────
    statusClassFor(inv) {
      const map = {
        draft: 'draft', sent: 'sent', processing: 'processing',
        pending: 'processing', error: 'error', corrected: 'corrected', voided: 'neutral',
      };
      return map[inv?.status] ?? 'neutral';
    },
    statusLabelFor(inv) {
      const map = {
        draft: this.$t('reservationInvoicePanel.status.draft'), sent: this.$t('reservationInvoicePanel.status.sent'), processing: this.$t('reservationInvoicePanel.status.processing'),
        pending: this.$t('reservationInvoicePanel.status.pending'), error: this.$t('reservationInvoicePanel.status.error'), corrected: this.$t('reservationInvoicePanel.status.corrected'), voided: this.$t('reservationInvoicePanel.status.voided'),
      };
      return map[inv?.status] ?? inv?.status ?? '—';
    },
    /**
     * ¿La factura ya está ASENTADA? Entonces se muestra en el listado "Facturas
     * emitidas" (arriba). Una recién emitida sigue "en curso" hasta que llega su PDF,
     * para no perder el sondeo automático del número/PDF.
     */
    isSettled(inv) {
      if (!inv) return false;
      return (inv.status === 'sent' && !!inv.pdf_url)
        || inv.status === 'corrected'
        || inv.status === 'voided';
    },

    // ─── Carga ──────────────────────────────────────────────────────────────
    async loadInvoice({ silent = false } = {}) {
      if (!silent) this.loading = true;
      try {
        const { data } = await axios.get(`/reservas/${this.reservaId}/invoices`);
        const invoices = data?.data ?? data ?? [];
        const mapped   = invoices.map(inv => this.mapInvoice(inv));
        // Asentadas → listado "Facturas emitidas" (arriba). En curso (borrador /
        // emitiéndose / error) → tarjeta de creación/emisión (abajo). Solo puede haber
        // una en curso a la vez (el backend lo garantiza).
        this.emittedInvoices = mapped.filter(inv => this.isSettled(inv));
        this.invoice         = mapped.find(inv => !this.isSettled(inv)) || null;
        this.previousInvoices = [];
        this.$emit('state-changed', this.invoice || this.emittedInvoices[0] || null);
        this.$emit('emitted-changed', this.emittedInvoices);
        this.ensureAutoSyncState();
      } catch {
        if (!silent) { this.invoice = null; this.emittedInvoices = []; this.previousInvoices = []; this.$emit('emitted-changed', []); }
      }
      finally { if (!silent) this.loading = false; }
    },

    mapInvoice(inv) {
      const customer = inv.customer ?? {};
      const billing  = inv.billing  ?? {};
      const invopop  = inv.invopop  ?? {};
      return {
        id:                   inv.id,
        uid:                  inv.uid,
        // De qué habitación es la factura. Nulo = de toda la reserva.
        room_instance_id:     inv.room_instance_id ?? null,
        room_label:           inv.room_label ?? null,
        status:               invopop.status ?? inv.invopop_status ?? inv.status ?? 'pending',
        // Tipo del DOCUMENTO (standard | credit-note). Sin esto, la vista de
        // cargos contaba la nota de crédito como una factura viva y escondía el
        // alojamiento después de anular.
        type:                 billing.type ?? inv.invoice_type ?? 'standard',
        customer_type:        customer.type    ?? inv.customer_type ?? 'individual',
        customer_name:        customer.name    ?? inv.customer_name,
        customer_tax_id:      customer.tax_id  ?? inv.customer_tax_id,
        customer_email:       customer.email   ?? inv.customer_email,
        customer_phone:       customer.phone   ?? inv.customer_phone,
        customer_country:     customer.country ?? inv.customer_country,
        customer_address:     customer.address ?? inv.customer_address,
        customer_city:        customer.city    ?? inv.customer_city,
        customer_state:       customer.state   ?? inv.customer_state,
        customer_zip:         customer.zip     ?? inv.customer_zip,
        currency:             billing.currency ?? inv.currency,
        total:                billing.total    ?? inv.total,
        // Lo cobrado y lo pendiente, tal y como lo calcula el motor que arma el PDF.
        // Viene hecho del backend a propósito: si lo recalculáramos aquí, la pantalla
        // y la factura volverían a poder decir cosas distintas.
        cobro:                inv.cobro ?? null,
        series:               billing.series   ?? inv.series ?? '',
        sequence_number:      billing.sequence_number ?? inv.sequence_number ?? null,
        display_code:         billing.display_code ?? inv.display_code ?? null,
        provisional_code:     billing.provisional_code ?? inv.provisional_code ?? null,
        draft_reference:      billing.draft_reference ?? inv.draft_reference ?? null,
        next_fiscal_estimate: billing.next_fiscal_estimate ?? inv.next_fiscal_estimate ?? null,
        fiscal_warning:       inv.fiscal_warning ?? null,
        pdf_url:              invopop.pdf_url  ?? inv.invopop_pdf_url ?? inv.pdf_url,
        error:                invopop.error    ?? inv.invopop_error   ?? inv.error,
        has_pdf:              invopop.has_pdf  ?? !!inv.invopop_pdf_url ?? false,
        payment_method:       inv.payment_method       ?? null,
        payment_method_label: inv.payment_method_label ?? null,
        notes:                inv.notes ?? null,
        sent_at:              inv.sent_at,
        created_at:           inv.created_at,
        // ── Corrección ──────────────────────────────────────────────────
        is_correction:          inv.is_correction          ?? false,
        corrected_invoice_id:   inv.corrected_invoice_id   ?? null,
        correction_reason:      inv.correction_reason      ?? null,
        corrected_display_code: inv.corrected_display_code ?? null,
      };
    },

    // ─── Enviar link al huésped ──────────────────────────────────────────────
    async sendInvoiceLink() {
      if (!this.reservaId || !this.canInvoice) return;
      this.generatingLink = true; this.linkAction = 'send';
      try {
        await axios.post(`/reservas/${this.reservaId}/invoices/send-link`, { send: true });
        $toast.success(this.$t('reservationInvoicePanel.toast.linkSent'));
        await this.loadInvoice();
      } catch (e) {
        if (e?.response?.status === 409) {
          const invoiceId = e?.response?.data?.invoice_id;
          if (invoiceId) {
            await this.sendIssuedInvoiceWhatsapp(invoiceId);
          } else {
            $toast.warning(this.$t('reservationInvoicePanel.toast.alreadyIssued'));
          }
        }
        else $toast.error(e?.response?.data?.message || this.$t('reservationInvoicePanel.toast.cantSendLink'));
      } finally { this.generatingLink = false; this.linkAction = null; }
    },

    // Confirmación previa: nunca se envía el link sin que el operador lo confirme.
    async confirmSendLink() {
      if (this.generatingLink) return;
      this.sendLinkModal.sending = true;
      try {
        await this.sendInvoiceLink();
        this.sendLinkModal.open = false;
      } finally {
        this.sendLinkModal.sending = false;
      }
    },

    // ─── Copiar link ─────────────────────────────────────────────────────────
    async copyInvoiceLink() {
      if (!this.reservaId || !this.canInvoice) return;
      this.generatingLink = true; this.linkAction = 'copy';
      try {
        const { data } = await axios.post(`/reservas/${this.reservaId}/invoices/send-link`, { send: false });
        if (data?.url) { await this.copyToClipboard(data.url); $toast.success(this.$t('reservationInvoicePanel.toast.linkCopied')); }
      } catch (e) {
        if (e?.response?.status === 409) { $toast.warning(this.$t('reservationInvoicePanel.toast.alreadyHasInvoice')); const pdfUrl = e?.response?.data?.pdf_url; if (pdfUrl) window.open(pdfUrl, '_blank'); }
        else $toast.error(e?.response?.data?.message || this.$t('reservationInvoicePanel.toast.cantGenerateLink'));
      } finally { this.generatingLink = false; this.linkAction = null; }
    },

    async copyToClipboard(text) {
      try { await navigator.clipboard.writeText(text); }
      catch { const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); }
    },

    // ─── Enviar por WhatsApp ─────────────────────────────────────────────────
    openWhatsappConfirmModal(inv) {
      const target = inv || this.invoice;
      if (!target || !this.canInvoice) return;
      this.whatsappModal = { open: true, sending: false, target };
    },

    async confirmSendInvoiceWhatsapp() {
      const target = this.whatsappModal.target || this.invoice;
      if (!target || !this.canInvoice) return;
      this.whatsappModal.sending = true;
      this.generatingLink = true;
      try {
        await this.sendIssuedInvoiceWhatsapp(target.id);
        this.whatsappModal.open = false;
      } catch (e) {
        $toast.error(e?.response?.data?.message || this.$t('reservationInvoicePanel.toast.cantSendWhatsapp'));
      } finally {
        this.whatsappModal.sending = false;
        this.generatingLink = false;
      }
    },

    async sendIssuedInvoiceWhatsapp(invoiceId) {
      const { data } = await axios.post(
        `/business/invoices/${invoiceId}/send-whatsapp`,
      );
      $toast.success(data?.message || this.$t('reservationInvoicePanel.toast.invoiceSentWhatsapp'));
      return data;
    },

    // ─── Crear factura directamente ──────────────────────────────────────────
    /**
     * Las habitaciones de la reserva, para ofrecer el fraccionado.
     *
     * Si falla, se queda en «no se puede fraccionar»: antes no ofrecer la opción que
     * ofrecerla y que al guardar reviente.
     */
    async loadBillableRooms() {
      try {
        const { data } = await axios.get(`/reservas/${this.reservaId}/billable-rooms`);
        this.billableRooms = {
          splittable: !!data.splittable,
          currency: data.currency || 'EUR',
          rooms: data.rooms || [],
        };
      } catch (e) {
        this.billableRooms = { splittable: false, currency: 'EUR', rooms: [] };
      }
    },

    /** Abre el modal de factura nueva ya sabiendo qué habitaciones hay. */
    async abrirNuevaFactura() {
      // Cada vez se parte de «toda la reserva»: es lo que espera quien no fracciona.
      this.scope.roomId = null;
      this.newInvoiceModal.open = true;
      await this.loadBillableRooms();
    },

    openCreateModal() {
      this.$refs.manualModal.open();
    },

    onInvoiceCreated(inv) {
      // Un borrador nuevo entra como factura EN CURSO; las emitidas siguen arriba.
      const mapped = this.mapInvoice(inv);
      if (this.isSettled(mapped)) this.loadInvoice({ silent: true });
      else this.invoice = mapped;
      this.$emit('state-changed', this.invoice);
      this.ensureAutoSyncState();
      this.$emit('invoice-created');
    },

    // ─── Confirmar crear otra factura (cargo posterior) ─────────────────────
    async confirmNewInvoice() {
      if (this.creatingDraft) return;
      await this.createDraft();
      if (!this.creatingDraft) this.newInvoiceModal.open = false;
    },

    // ─── Borrador → emitir ───────────────────────────────────────────────────
    async createDraft() {
      if (this.creatingDraft) return;
      this.creatingDraft = true;
      try {
        const { data } = await axios.post(
          `/reservas/${this.reservaId}/invoices/draft`,
          // Sin habitación va la factura de siempre, la de toda la reserva.
          this.scope.roomId ? { room_instance_id: this.scope.roomId } : {},
        );
        // Deja las emitidas arriba; el borrador nuevo entra como factura en curso.
        this.invoice = this.mapInvoice(data.data ?? data);
        this.$emit('state-changed', this.invoice);
        $toast.success(this.emittedInvoices.length
          ? this.$t('reservationInvoicePanel.toast.draftCreatedNewCharges')
          : this.$t('reservationInvoicePanel.toast.draftCreated'));
      } catch (e) {
        if (e?.response?.status === 409) { $toast.warning(e?.response?.data?.message || this.$t('reservationInvoicePanel.toast.alreadyInProgress')); await this.loadInvoice(); }
        else $toast.error(e?.response?.data?.message || this.$t('reservationInvoicePanel.toast.cantCreateDraft'));
      } finally {
        this.creatingDraft = false;
      }
    },

    async confirmEmit() {
      if (!this.invoice || this.emitting) return;
      this.emitting = true;
      try {
        const { data } = await axios.post(`/business/invoices/${this.invoice.id}/emit`);
        const mapped = this.mapInvoice(data);
        this.emitModal.open = false;
        if (this.isSettled(mapped)) {
          await this.loadInvoice({ silent: true });
        } else {
          this.invoice = mapped;
          this.$emit('state-changed', this.invoice);
          this.ensureAutoSyncState();
        }
        $toast.success(this.$t('reservationInvoicePanel.toast.invoiceEmitted'));
      } catch (e) {
        $toast.error(e?.response?.data?.message || this.$t('reservationInvoicePanel.toast.cantEmit'));
      } finally {
        this.emitting = false;
      }
    },

    // ─── Vista previa PDF (proforma) del borrador ────────────────────────────
    async previewProforma() {
      if (!this.invoice || this.previewing) return;
      this.previewing = true;
      try {
        const { data } = await axios.get(`/business/invoices/${this.invoice.id}/proforma`, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (e) {
        // La respuesta de error viene como blob → intentamos leer el JSON.
        let msg = this.$t('reservationInvoicePanel.toast.cantPreview');
        try { const t = await e?.response?.data?.text?.(); if (t) msg = JSON.parse(t)?.message || msg; } catch (_) {}
        $toast.error(msg);
      } finally {
        this.previewing = false;
      }
    },

    // ─── Mandarle el presupuesto al cliente por correo ───────────────────────
    async enviarPresupuesto() {
      if (!this.invoice || this.enviandoPresupuesto) return;
      this.enviandoPresupuesto = true;
      try {
        const { data } = await axios.post(`/business/invoices/${this.invoice.id}/proforma/send`);
        // Se dice A QUIÉN se mandó: el correo puede venir de la ficha del cliente
        // o de la reserva, y el anfitrión no tiene por qué saber cuál cogió.
        $toast.success(this.$t('reservationInvoicePanel.toast.quoteSent', { email: data?.email || '' }));
      } catch (e) {
        $toast.error(e?.response?.data?.message || this.$t('reservationInvoicePanel.toast.cantSendQuote'));
      } finally {
        this.enviandoPresupuesto = false;
      }
    },

    // ─── Sincronizar estado ──────────────────────────────────────────────────
    async syncInvoice({ silent = false } = {}) {
      if (!this.invoice) return;
      if (!silent) this.syncing = true;
      try {
        const { data } = await axios.post(`/business/invoices/${this.invoice.id}/sync`);
        const mapped = this.mapInvoice(data);
        if (this.isSettled(mapped)) {
          // Ya asentada (emitida con PDF) → pasa al listado "Facturas emitidas".
          await this.loadInvoice({ silent: true });
        } else {
          this.invoice = mapped;
          this.$emit('state-changed', this.invoice);
          this.ensureAutoSyncState();
        }
      } catch {
        if (!silent) $toast.error(this.$t('reservationInvoicePanel.toast.cantSync'));
      } finally {
        if (!silent) this.syncing = false;
      }
    },

    // ─── Reintentar factura con error ────────────────────────────────────────
    async retryInvoice() {
      if (!this.invoice) return; this.retrying = true;
      try {
        const { data } = await axios.post(`/business/invoices/${this.invoice.id}/retry`);
        this.invoice = this.mapInvoice(data);
        this.$emit('state-changed', this.invoice);
        this.ensureAutoSyncState();
        $toast.success(this.$t('reservationInvoicePanel.toast.retrying'));
      } catch (e) { $toast.error(e?.response?.data?.message || this.$t('reservationInvoicePanel.toast.cantRetry')); }
      finally { this.retrying = false; }
    },

    // Confirmación previa: reintentar reenvía la factura a la autoridad de facturación.
    async confirmRetry() {
      if (this.retrying) return;
      await this.retryInvoice();
      this.retryModal.open = false;
    },

    // ─── Anular factura / Abono ──────────────────────────────────────────────
    openVoidModal(inv) { this.voidModal = { open: true, voiding: false, done: false, creditNoteSeries: null, target: inv || this.invoice }; },
    async confirmVoid() {
      const target = this.voidModal.target || this.invoice;
      if (!target) return; this.voidModal.voiding = true;
      try {
        const { data } = await axios.post(`/business/invoices/${target.id}/void`, {
          reason: 'Anulación para reemisión con datos o cargos corregidos',
        });
        this.voidModal.done = true;
        this.voidModal.creditNoteSeries = data.credit_note_series || 'NC';
        await this.loadInvoice();
        this.$emit('charges-updated');
      } catch (e) {
        $toast.error(e?.response?.data?.message || this.$t('reservationInvoicePanel.toast.cantVoid'));
        this.voidModal.open = false;
      } finally { this.voidModal.voiding = false; }
    },

    // ─── Editar datos del cliente ────────────────────────────────────────────
    openEditModal() {
      this.editModal.form = {
        customer_type:    this.invoice?.customer_type    || 'individual',
        customer_name:    this.invoice?.customer_name    || '',
        customer_tax_id:  this.invoice?.customer_tax_id  || '',
        customer_country: this.invoice?.customer_country || '',
        customer_email:   this.invoice?.customer_email   || '',
        customer_phone:   this.invoice?.customer_phone   || '',
        customer_address: this.invoice?.customer_address || '',
        customer_city:    this.invoice?.customer_city    || '',
        customer_state:   this.invoice?.customer_state   || '',
        customer_zip:     this.invoice?.customer_zip     || '',
      };
      this.editModal.open = true;
      this.editModal.saving = false;
      // Teletransporta el overlay a <body> para que escape del stacking context
      // del drawer de la reserva (si no, los iconos flotantes quedan por encima).
      this.$nextTick(() => {
        const el = this.$refs.editOverlay;
        if (el && el.parentNode !== document.body) {
          document.body.appendChild(el);
          this._editOverlayEl = el;
        }
      });
    },
    async saveAndRetry() {
      if (!this.invoice) return; this.editModal.saving = true;
      try {
        const { data } = await axios.put(`/business/invoices/${this.invoice.id}/customer`, this.editModal.form);
        this.invoice = this.mapInvoice(data);
        if (this.invoice.fiscal_warning) {
          // Se GUARDÓ, pero el documento fiscal no es válido: NO cerramos el modal,
          // mostramos el aviso dentro para que el operador lo corrija en el momento
          // (igual que al crear el borrador). Los datos NO se pierden.
          $toast.warning(this.$t('reservationInvoicePanel.toast.savedCheckFiscal'));
        } else {
          this.editModal.open = false;
          $toast.success(this.$t('reservationInvoicePanel.toast.dataUpdated'));
        }
      } catch (e) { $toast.error(e?.response?.data?.message || this.$t('reservationInvoicePanel.toast.cantSaveData')); }
      finally { this.editModal.saving = false; }
    },

    // ─── Eliminar factura ────────────────────────────────────────────────────
    openDeleteModal() { this.deleteModal = { open: true, deleting: false }; },
    async confirmDelete() {
      if (!this.invoice) return; this.deleteModal.deleting = true;
      try {
        await axios.delete(`/business/invoices/${this.invoice.id}`);
        this.invoice = null;
        this.$emit('state-changed', this.invoice);
        this.stopAutoSyncPolling();
        this.deleteModal.open = false;
        $toast.success(this.$t('reservationInvoicePanel.toast.invoiceDeleted'));
        this.$emit('charges-updated');
      } catch (e) { $toast.error(e?.response?.data?.message || this.$t('reservationInvoicePanel.toast.cantDelete')); }
      finally { this.deleteModal.deleting = false; }
    },

    // ─── Corregir factura (rectificativa) ───────────────────────────────────
    openCorrectionModal(inv) {
      const target = inv || this.invoice;
      if (!target) return;
      this.correctionModal = {
        ...this.correctionModal,
        open:         true,
        saving:       false,
        done:         false,
        corrective:   null,
        customReason: '',
        target,
        form: {
          customer_name:     target.customer_name    || '',
          customer_tax_id:   target.customer_tax_id  || '',
          customer_country:  target.customer_country || '',
          customer_email:    target.customer_email   || '',
          customer_address:  target.customer_address || '',
          customer_city:     target.customer_city    || '',
          customer_state:    target.customer_state   || '',
          customer_zip:      target.customer_zip     || '',
          correction_reason: '',
          payment_method:    target.payment_method   || '',
        },
      };
    },

    async confirmCorrection() {
      const target = this.correctionModal.target || this.invoice;
      if (!target) return;
      this.correctionModal.saving = true;
      try {
        const payload = { ...this.correctionModal.form };
        // Si el operador eligió "Otro", usamos el campo libre
        if (payload.correction_reason === '__custom__') {
          payload.correction_reason = this.correctionModal.customReason || '';
        }
        const { data } = await axios.post(
          `/business/invoices/${target.id}/correct`,
          payload,
        );
        this.correctionModal.corrective = this.mapInvoice(data);
        this.correctionModal.done       = true;
        // Recargar la lista para mostrar la rectificativa como activa
        // y la original en el historial
        await this.loadInvoice();
        this.ensureAutoSyncState();
        this.$emit('charges-updated');
      } catch (e) {
        $toast.error(e?.response?.data?.message || this.$t('reservationInvoicePanel.toast.cantEmitRectif'));
        this.correctionModal.open = false;
      } finally {
        this.correctionModal.saving = false;
      }
    },

    ensureAutoSyncState() {
      if (!this.invoice) {
        this.stopAutoSyncPolling();
        return;
      }

      if (
        ['pending', 'processing'].includes(this.invoice.status) ||
        (this.invoice.status === 'sent' && !this.invoice.pdf_url)
      ) {
        this.startAutoSyncPolling();
        return;
      }

      this.stopAutoSyncPolling();
    },

    startAutoSyncPolling() {
      if (
        !this.invoice ||
        (
          !['pending', 'processing'].includes(this.invoice.status) &&
          !(this.invoice.status === 'sent' && !this.invoice.pdf_url)
        )
      ) {
        return;
      }

      if (this.autoSyncTimer) return;

      this.autoSyncTicks = 0;
      this.autoSyncTimer = window.setInterval(async () => {
        this.autoSyncTicks += 1;

        await this.syncInvoice({ silent: true });

        if (
          !this.invoice ||
          (
            !['pending', 'processing'].includes(this.invoice.status) &&
            !(this.invoice.status === 'sent' && !this.invoice.pdf_url)
          ) ||
          this.autoSyncTicks >= 10
        ) {
          this.stopAutoSyncPolling();
        }
      }, 2000);
    },

    stopAutoSyncPolling() {
      if (!this.autoSyncTimer) return;
      window.clearInterval(this.autoSyncTimer);
      this.autoSyncTimer = null;
      this.autoSyncTicks = 0;
    },

    // ─── Helpers de formato ──────────────────────────────────────────────────
    formatNum(n) {
      return parseFloat(n || 0).toLocaleString('es-ES', { useGrouping: "always", minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    /* Cantidades: «4», no «4,00». Pero si traen decimales (noches partidas, tasas por
       persona), se enseñan los que haya hasta 2, sin inventarse ceros.
       Sin valor → «—», como en la tabla de cargos: un descuento no tiene cantidad, y
       pintarle un 0 o un 1 sería mentir sobre el desglose. */
    formatQty(n) {
      const v = parseFloat(n);
      if (!Number.isFinite(v)) return '—';
      return v.toLocaleString('es-ES', { useGrouping: "always", minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },

    // Precio unitario: «—» si la línea no tiene uno (no «0,00», que se lee como gratis).
    formatUnit(n) {
      const v = parseFloat(n);
      return Number.isFinite(v) ? this.formatNum(v) : '—';
    },

    formatVat(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? `${n}%` : '—';
    },

    /**
     * Formato de fecha en español con formato europeo: 15 ene. 2025
     * Usa locale es-ES para garantizar nombres de meses en español.
     */
    /* La fecha de un cobro viene como 'AAAA-MM-DD' pelado, y `new Date('2026-10-24')`
       se interpreta en UTC: en Brasil (UTC-3) se pintaba «23 oct» mientras el PDF
       decía 24/10/2026. Un día de diferencia entre la pantalla y la factura es
       justo lo que este bloque viene a quitar, así que se trocea a mano. */
    formatFechaCobro(d) {
      if (!d) return '';
      const [a, m, dia] = String(d).slice(0, 10).split('-').map(Number);
      if (!a || !m || !dia) return String(d);
      return new Date(a, m - 1, dia).toLocaleDateString('es-ES', {
        day: '2-digit', month: 'short', year: 'numeric',
      });
    },
    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('es-ES', {
        day:   '2-digit',
        month: 'short',
        year:  'numeric',
      });
    },
  },
};
</script>

<style scoped>

/* El distintivo de la habitación en una factura fraccionada. Discreto: informa,
   no compite con el estado ni con el número. */
.rip-room-chip {
  font-family: "DM Sans", sans-serif;
  font-size: 11.5px; font-weight: 600; color: #4A4E57;
  background: #F1F1F4; border-radius: 999px; padding: 3px 9px;
  white-space: nowrap;
}
.rip-room-chip--sm { font-size: 10.5px; padding: 2px 7px; }

/* ── Entera o por habitación ──────────────────────────────────────────────
   Tarjetas planas, sin aro de color: lo elegido se distingue por el fondo y el
   peso del texto, que es más tranquilo de leer en un modal pequeño. */
.rip-scope { margin-top: 16px; display: flex; flex-direction: column; gap: 8px; }

.rip-scope__label {
  font-family: "DM Sans", sans-serif;
  font-size: 11px; font-weight: 600; letter-spacing: .04em;
  text-transform: uppercase; color: #8A8F98; margin-bottom: 2px;
}
.rip-scope__label--sub { text-transform: none; letter-spacing: 0; font-size: 12px; margin-top: 4px; }

.rip-scope__opt {
  width: 100%; text-align: left; border: 1px solid #E8E9EC; background: #fff;
  border-radius: 12px; padding: 11px 13px; cursor: pointer;
  display: flex; flex-direction: column; gap: 2px;
  transition: background .16s ease, border-color .16s ease;
}
.rip-scope__opt:hover { background: #FAFAFB; }
.rip-scope__opt.is-on { background: #F4F4F6; border-color: #D8D9DE; }

.rip-scope__name {
  font-family: "DM Sans", sans-serif; font-size: 14px; font-weight: 600; color: #16181D;
}
.rip-scope__hint {
  font-family: "DM Sans", sans-serif; font-size: 12.5px; color: #71757E; line-height: 1.45;
}

.rip-scope__rooms {
  display: flex; flex-direction: column; gap: 6px;
  padding: 10px 0 2px 0;
}
.rip-scope__room {
  width: 100%; display: flex; align-items: baseline; justify-content: space-between; gap: 12px;
  border: 1px solid #E8E9EC; background: #fff; border-radius: 10px;
  padding: 9px 12px; cursor: pointer;
  transition: background .16s ease, border-color .16s ease;
}
.rip-scope__room:hover { background: #FAFAFB; }
.rip-scope__room.is-on { background: #F4F4F6; border-color: #D8D9DE; }

.rip-scope__roomName {
  font-family: "DM Sans", sans-serif; font-size: 13.5px; font-weight: 600; color: #16181D;
}
.rip-scope__roomAmount {
  font-family: "DM Sans", sans-serif; font-size: 12px; color: #71757E; white-space: nowrap;
}

@media (prefers-reduced-motion: reduce) {
  .rip-scope__opt, .rip-scope__room { transition: none; }
}

/* El overlay de los modales se TELETRANSPORTA a <body> (para escapar del drawer),
   por eso .rip-overlay debe llevar las mismas variables que .rip: si no, al salir
   de .rip pierde --brand/--ink y el botón "Guardar cambios" quedaba invisible. */
.rip,
.rip-overlay {
  --brand:   #5e71e4;
  --ink:     #0f172a;
  --ink-2:   #1e293b;
  --ink-3:   #475569;
  --ink-4:   #94a3b8;
  --ink-5:   #cbd5e1;
  --ink-6:   #e2e8f0;
  --ink-7:   #f1f5f9;
  --ink-8:   #f8fafc;
  --red:     #dc2626;
  --red-lt:  #fef2f2;
  --green:   #16a34a;
  --green-lt:#f0fdf4;
  --blue:    #2563eb;
  --wa:      #11b14d;  /* WhatsApp green */
  --r:       12px;
  --rs:      8px;
  --shadow:  0 1px 3px rgba(15,23,42,.06), 0 4px 14px rgba(15,23,42,.06);
  --shadow-sm: 0 1px 2px rgba(15,23,42,.05), 0 2px 8px rgba(15,23,42,.04);
  font-family: 'DM Sans', system-ui, sans-serif;
}

/* ─── Spinner ──────────────────────────────────────────────────────────────── */
.rip-spinner {
  display: inline-block; width: 14px; height: 14px;
  border: 2px solid var(--ink-6); border-top-color: var(--brand);
  border-radius: 50%; animation: rip-spin .7s linear infinite;
}
.rip-spinner--sm    { width: 11px; height: 11px; border-width: 1.8px; }
.rip-spinner--white { border-color: rgba(255,255,255,.25); border-top-color: #fff; }
@keyframes rip-spin { to { transform: rotate(360deg); } }

/* ─── Loading ──────────────────────────────────────────────────────────────── */
.rip-loading { display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--ink-3); }

/* ─── Dot de estado ────────────────────────────────────────────────────────── */
.rip-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; display: inline-block; }
.rip-dot--sent       { background: var(--green); }
.rip-dot--processing { background: var(--blue); animation: rip-pulse 1.6s ease-in-out infinite; }
.rip-dot--error      { background: var(--red); }
.rip-dot--neutral    { background: var(--ink-5); }
@keyframes rip-pulse { 0%,100%{opacity:1}50%{opacity:.3} }

/* ─── Estado vacío ─────────────────────────────────────────────────────────── */
/* Layout en COLUMNA: la info arriba y las acciones debajo (más limpio/pro). */
.rip-empty {
  display: flex; flex-direction: column; align-items: stretch;
  gap: 14px;
  background: #fff; box-shadow: var(--shadow);
  border-radius: var(--r); padding: 18px;
}
.rip-empty__info   { display: flex; align-items: flex-start; gap: 11px; min-width: 0; }
.rip-empty__title  { margin: 0 0 3px; font-size: 13.5px; font-weight: 700; color: var(--ink); letter-spacing: -.01em; }
.rip-empty__sub    { margin: 0; font-size: 12px; color: var(--ink-4); line-height: 1.5; }
.rip-empty__hint   { margin: 0; font-size: 12px; color: var(--ink-4); font-style: italic; line-height: 1.5; }
.rip-empty__actions{ display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.rip-empty__info .rip-dot { margin-top: 5px; }

/* ─── Solicitada por el huésped (pendiente de emitir) ─────────────────────── */
.rip-empty--requested { border: 1px solid #fde7c8; background: linear-gradient(180deg, #fffaf3, #fff); }
.rip-dot--requested   { background: #f59e0b; box-shadow: 0 0 0 3px #fef3e2; }
.rip-req-badge {
  display: inline-block; margin-left: 7px; padding: 1px 8px; border-radius: 999px;
  font-size: 10px; font-weight: 700; color: #b45309; background: #fff7ed;
  border: 1px solid #fed7aa; vertical-align: middle;
}
.rip-req-fiscal {
  display: flex; flex-direction: column; gap: 1px;
  background: #fbfaf7; border: 1px solid #f1ece3; border-radius: 10px; padding: 9px 12px;
}
.rip-req-fiscal__row { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; padding: 3px 0; font-size: 12px; }
.rip-req-fiscal__k { color: var(--ink-4); flex-shrink: 0; }
.rip-req-fiscal__v { color: var(--ink); font-weight: 600; text-align: right; word-break: break-word; }

/* ─── Card principal ───────────────────────────────────────────────────────── */
.rip-card {
  background: #fff;
  box-shadow: var(--shadow);
  border-radius: var(--r);
  overflow: hidden;
}

/* Cabecera */
.rip-card__head {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 11px 16px;
  border-bottom: 1px solid var(--ink-7);
}
.rip-head__left { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

/* Status chip */
.rip-status-chip {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .08em;
  padding: 3px 9px; border-radius: 99px;
}
.rip-status-chip__dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
.rip-status-chip--sent       { background: #f0fdf4; color: var(--green); }
.rip-status-chip--sent .rip-status-chip__dot { background: var(--green); }
.rip-status-chip--processing { background: #eff6ff; color: var(--blue); }
.rip-status-chip--processing .rip-status-chip__dot { background: var(--blue); animation: rip-pulse 1.6s ease-in-out infinite; }
.rip-status-chip--error      { background: var(--red-lt); color: var(--red); }
.rip-status-chip--error .rip-status-chip__dot { background: var(--red); }
.rip-status-chip--corrected  { background: #fef3c7; color: #92400e; }
.rip-status-chip--corrected .rip-status-chip__dot { background: #d97706; }
.rip-status-chip--neutral    { background: var(--ink-7); color: var(--ink-3); }
.rip-status-chip--neutral .rip-status-chip__dot { background: var(--ink-4); }
.rip-status-chip--draft      { background: #fff7ed; color: #ea580c; }
.rip-status-chip--draft .rip-status-chip__dot { background: #ea580c; }

/* Invoice code — badge sutil (se lee como número de factura) */
.rip-inv-code {
  font-size: 12px; font-weight: 700; color: var(--ink-2);
  font-family: 'DM Mono', 'Fira Mono', 'Courier New', monospace;
  letter-spacing: .02em;
  background: var(--ink-8); padding: 3px 9px; border-radius: 7px;
}
/* Referencia de borrador: no es número fiscal → estilo más tenue y punteado. */
.rip-inv-code--draft {
  color: var(--ink-4); font-weight: 600; background: transparent;
  border: 1px dashed var(--ink-6);
}

/* Botón PDF */
.rip-pdf-btn {
  display: inline-flex; align-items: center; gap: 5px; flex-shrink: 0;
  font-size: 11.5px; font-weight: 600; color: var(--brand); text-decoration: none;
  padding: 5px 11px; border-radius: 7px;
  background: rgba(94,113,228,.07);
  transition: all .15s;
}
.rip-pdf-btn:hover { background: rgba(94,113,228,.14); }

/* Cuerpo */
.rip-card__body {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; padding: 16px 18px;
}
.rip-card__client { flex: 1; min-width: 0; }
.rip-client-name {
  margin: 0 0 8px; font-size: 15px; font-weight: 700;
  color: var(--ink); letter-spacing: -.02em; line-height: 1.2;
}
.rip-client-meta { display: flex; flex-wrap: wrap; gap: 5px; }
.rip-meta-pill {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; color: var(--ink-3);
  padding: 3px 9px; border-radius: 99px;
  background: var(--ink-7);
}
.rip-meta-pill--payment {
  color: #6d28d9;
  background: #f5f3ff;
}

/* Total */
.rip-card__total {
  display: flex; flex-direction: column; align-items: flex-end;
  flex-shrink: 0; gap: 1px;
}
.rip-total-cur {
  font-size: 9px; font-weight: 700; color: var(--ink-4);
  text-transform: uppercase; letter-spacing: .07em;
}
.rip-total-num {
  font-size: 22px; font-weight: 800; color: var(--ink);
  letter-spacing: -.04em; line-height: 1;
}

/* ─── Conceptos del documento ──────────────────────────────────────────────────
   Mismo lenguaje visual que el detalle de las facturas emitidas (bandeja gris,
   12px, importes tabulares a la derecha), con dos columnas más —cantidad y precio
   unitario— porque es lo que se mira al cuadrar un documento. */
.rip-concepts {
  margin: 0 18px 12px; padding: 10px 12px;
  background: var(--ink-8); border-radius: 9px;
}
.rip-concepts__title {
  display: flex; align-items: center; gap: 6px; margin: 0 0 7px;
  font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .08em;
  color: var(--ink-4);
}
.rip-concepts__count {
  min-width: 16px; height: 16px; padding: 0 5px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--ink-6); color: var(--ink-3);
  border-radius: 9px; font-size: 10px; font-weight: 800; letter-spacing: 0;
}

/* La rejilla la comparten cabecera y filas: así los números quedan alineados. */
.rip-concepts__head,
.rip-concept {
  display: grid;
  grid-template-columns: 1fr 34px 64px 38px 78px;
  gap: 8px; align-items: baseline;
}
.rip-concepts__head {
  padding-bottom: 5px; border-bottom: 1px solid var(--ink-6);
  font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em;
  color: var(--ink-4);
}
.rip-concepts__head span + span { text-align: right; }

.rip-concept { padding: 5px 0; font-size: 12px; }
.rip-concept + .rip-concept { border-top: 1px solid var(--ink-7); }
.rip-concept__name { color: var(--ink-2); min-width: 0; }
.rip-concept__date { margin-left: 6px; font-size: 10.5px; color: var(--ink-4); }
.rip-concept__num {
  text-align: right; color: var(--ink-3); white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.rip-concept__vat { color: var(--ink-4); font-size: 11px; }
.rip-concept__amount { color: var(--ink-2); font-weight: 600; }
.rip-concept__amount--neg { color: var(--green); }

.rip-concepts__foot {
  display: flex; align-items: baseline; justify-content: space-between; gap: 10px;
  margin-top: 6px; padding-top: 7px; border-top: 1px solid var(--ink-6);
}
.rip-concepts__foot-label {
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em;
  color: var(--ink-4);
}
.rip-concepts__foot-total {
  font-size: 13px; font-weight: 800; color: var(--ink); white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.rip-concepts__hint { margin: 6px 0 0; font-size: 10.5px; color: var(--ink-4); line-height: 1.4; }
/* Si el desglose no suma el total del documento, se avisa: no se maquilla. */
.rip-concepts__warn {
  display: flex; align-items: flex-start; gap: 6px;
  margin: 8px 0 0; padding: 8px 10px;
  background: #fffbeb; box-shadow: 0 0 0 1px #fde68a;
  border-radius: var(--rs);
  font-size: 11px; color: #92400e; line-height: 1.45;
}

/* Cobro: lo pagado y lo que queda a deber. Mismo bloque y mismo orden que el pie
   del PDF (total → anticipos → adeudado), para que leer una cosa y la otra no
   obligue a traducir nada. */
.rip-cobro {
  margin: 0 18px 12px; padding: 10px 12px;
  background: var(--ink-8); border-radius: 9px;
}
.rip-cobro__row {
  display: flex; align-items: baseline; justify-content: space-between; gap: 10px;
  padding: 4px 0; font-size: 12px; color: var(--ink-2);
}
.rip-cobro__num {
  text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums;
}
.rip-cobro__date { margin-left: 6px; font-size: 10.5px; color: var(--ink-4); }
.rip-cobro__row--paid { color: var(--ink-3); }
.rip-cobro__row--paid .rip-cobro__num { color: var(--green); }
.rip-cobro__row--due {
  margin-top: 4px; padding-top: 7px; border-top: 1px solid var(--ink-6);
  font-size: 13px; font-weight: 800; color: var(--ink);
}
.rip-cobro__row--due .rip-cobro__num { color: var(--ink); }
.rip-cobro__row--settled,
.rip-cobro__row--settled .rip-cobro__num { color: var(--green); }
.rip-cobro__warn {
  display: flex; align-items: flex-start; gap: 6px;
  margin: 8px 0 0; padding: 8px 10px;
  background: #fffbeb; box-shadow: 0 0 0 1px #fde68a;
  border-radius: var(--rs);
  font-size: 11px; color: #92400e; line-height: 1.45;
}

/* Notas */
.rip-notes {
  display: flex; align-items: flex-start; gap: 6px;
  margin: 0 18px 12px; padding: 9px 12px;
  background: var(--ink-8); border-radius: var(--rs);
  font-size: 11.5px; color: var(--ink-3); line-height: 1.5;
}

/* Error */
.rip-error-msg {
  display: flex; align-items: flex-start; gap: 6px;
  margin: 0 18px 14px; padding: 10px 13px;
  background: var(--red-lt);
  box-shadow: 0 0 0 1px #fecaca;
  border-radius: var(--rs); font-size: 11.5px; color: #991b1b; line-height: 1.5;
}

/* Acciones */
.rip-card__actions {
  display: flex; align-items: center; gap: 7px; flex-wrap: wrap;
  padding: 10px 16px;
  border-top: 1px solid var(--ink-7);
}

/* ─── Botones ──────────────────────────────────────────────────────────────── */
.rip-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 8px 14px; border-radius: var(--rs);
  font-size: 13px; font-weight: 600; cursor: pointer;
  border: 1px solid transparent; text-decoration: none;
  transition: all .15s; white-space: nowrap; font-family: inherit;
  letter-spacing: -.01em;
}
.rip-btn:disabled { opacity: .45; cursor: not-allowed; }
.rip-btn--full { width: 100%; justify-content: center; }

.rip-btn--brand {
  background: var(--brand); color: #fff;
  box-shadow: 0 4px 12px -4px rgba(94,113,228,.5);
}
/* Botón de EMISIÓN: verde (acción de confirmar), más didáctico que el morado. */
.rip-btn--emit {
  background: var(--green); color: #fff;
  box-shadow: 0 4px 12px -4px rgba(22,163,74,.5);
}
.rip-btn--emit:hover:not(:disabled) { background: #15803d; }
.rip-modal__icon--emit { background: rgba(22,163,74,.1); color: var(--green); }
.rip-emit-steps {
  list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 9px;
  font-size: 12.5px; color: var(--ink-2); line-height: 1.5;
}
/* El check va POSICIONADO a la izquierda y el texto fluye normal (párrafo), para que
   la negrita quede en línea y no se rompa en columnas (el li NO es flex a propósito). */
.rip-emit-steps li { position: relative; padding-left: 22px; line-height: 1.55; }
.rip-emit-steps svg { position: absolute; left: 0; top: 4px; flex-shrink: 0; color: var(--green); }
.rip-emit-steps b { color: var(--ink); font-weight: 700; }
.rip-emit-warn {
  display: flex; align-items: flex-start; gap: 8px; margin-top: 13px;
  background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px; padding: 10px 12px;
  font-size: 12px; color: #92400e; line-height: 1.5;
}
.rip-emit-warn b { font-weight: 700; }
.rip-btn--brand:hover:not(:disabled) {
  background: #4a5fd4;
  box-shadow: 0 6px 16px -5px rgba(94,113,228,.55);
  transform: translateY(-1px);
}

/* Secundario: blanco con borde suave (mismo patrón que el modal / "Ver recibo"). */
.rip-btn--ghost {
  background: #fff; color: var(--ink-2);
  border-color: var(--ink-6);
}
.rip-btn--ghost:hover:not(:disabled) { background: var(--ink-8); border-color: var(--ink-5); }

.rip-btn--icon {
  background: #fff; color: var(--ink-3);
  border-color: var(--ink-6);
  padding: 8px 10px;
}
.rip-btn--icon:hover:not(:disabled) { background: var(--ink-8); color: var(--ink); border-color: var(--ink-5); }

.rip-btn--whatsapp {
  background: var(--wa); color: #fff;
  box-shadow: 0 4px 12px -4px rgba(17,177,77,.5);
}
.rip-btn--whatsapp:hover:not(:disabled) {
  background: #0d9640;
  box-shadow: 0 6px 16px -5px rgba(17,177,77,.55);
  transform: translateY(-1px);
}

.rip-btn--danger-ghost {
  background: transparent; color: var(--red);
  padding-left: 7px; padding-right: 7px;
}
.rip-btn--danger-ghost:hover:not(:disabled) { background: var(--red-lt); }

.rip-btn--danger {
  background: var(--red); color: #fff;
  box-shadow: 0 2px 8px rgba(220,38,38,.22);
}
.rip-btn--danger:hover:not(:disabled) { background: #b91c1c; }

/* ─── Modales ──────────────────────────────────────────────────────────────── */
.rip-overlay {
  position: fixed; inset: 0; background: rgba(15,23,42,.42);
  backdrop-filter: blur(4px); z-index: 100070; /* por encima del scrim del drawer (100000) */
  display: flex; align-items: center; justify-content: center; padding: 20px;
}

.rip-modal {
  background: #fff; border-radius: 16px; width: 100%; max-width: 480px;
  box-shadow: 0 24px 64px rgba(15,23,42,.2), 0 4px 16px rgba(15,23,42,.08);
  animation: rip-pop .2s ease;
  display: flex; flex-direction: column; max-height: 90vh; overflow: hidden;
}
.rip-modal--sm  { max-width: 380px; }
.rip-modal--lg  { max-width: 620px; }
.rip-modal--xl  { max-width: 640px; }
@keyframes rip-pop { from{transform:scale(.96);opacity:0} to{transform:scale(1);opacity:1} }

.rip-modal__head {
  display: flex; align-items: center; gap: 12px;
  padding: 18px 22px 14px; border-bottom: 1px solid var(--ink-7); flex-shrink: 0;
  background: var(--ink-8);
}
.rip-modal__head--success { background: var(--green-lt); border-bottom-color: #bbf7d0; }

.rip-modal__icon {
  width: 36px; height: 36px; background: rgba(94,113,228,.1); color: var(--brand);
  border-radius: 10px; display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; font-size: 15px;
}
.rip-modal__icon--success { background: rgba(22,163,74,.1); color: var(--green); }

.rip-modal__title { margin: 0 0 2px; font-size: 14px; font-weight: 700; color: var(--ink); }
.rip-modal__sub   { margin: 0; font-size: 12px; color: var(--ink-4); }
.rip-modal__x {
  background: none; border: none; cursor: pointer; color: var(--ink-4);
  padding: 5px; border-radius: 7px; display: flex; align-items: center;
  transition: all .12s; flex-shrink: 0; margin-left: auto;
}
.rip-modal__x:hover { background: var(--ink-6); color: var(--ink-2); }
.rip-modal__x:disabled { opacity: .4; cursor: not-allowed; }

.rip-modal__body { padding: 18px 22px; overflow-y: auto; flex: 1; display: flex; flex-direction: column; gap: 12px; }
.rip-modal__body--scroll { overflow-y: auto; }
.rip-modal__desc { margin: 0; font-size: 13px; color: var(--ink-2); line-height: 1.65; }
.rip-modal__hint { margin: 0; font-size: 12px; color: var(--ink-4); line-height: 1.5; }

.rip-modal__foot {
  display: flex; align-items: center; justify-content: flex-end; gap: 8px;
  padding: 14px 22px; border-top: 1px solid var(--ink-7);
  background: var(--ink-8); flex-shrink: 0;
}

/* ─── Formulario "Editar factura" — etiqueta a la izquierda, igual que el
       modal de crear factura (ManualInvoiceCreateModal): pareja label|input
       alineada, sin ambigüedad de a qué input pertenece cada etiqueta. ─────── */
.rip-form { display: flex; flex-direction: column; }
.rip-field {
  display: grid; grid-template-columns: 176px 1fr;
  align-items: start; column-gap: 16px;
  padding: 8px 0; border-bottom: 1px solid var(--ink-8);
}
.rip-field:last-child { border-bottom: none; }
.rip-label {
  grid-column: 1; grid-row: 1; padding-top: 11px; margin-bottom: 0;
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .02em; line-height: 1.35; color: var(--ink-2);
}
.rip-opt { font-weight: 600; color: var(--ink-4); text-transform: none; letter-spacing: 0; }
.rip-field > .rip-input { grid-column: 2; grid-row: 1; }
/* Selector de país con buscador (mismo que el modal de crear) */
.rip-field > .rip-countrywrap { grid-column: 2; grid-row: 1; }
/* Toggle Particular / Empresa (mismo estilo que el modal de crear) */
.rip-field > .rip-typetoggle { grid-column: 2; grid-row: 1; }
.rip-typetoggle { display: flex; gap: 8px; }
.rip-typebtn {
  flex: 1; display: flex; flex-direction: column; align-items: flex-start; gap: 1px;
  padding: 7px 12px; border: 1.5px solid var(--ink-6); background: #fff;
  border-radius: 10px; cursor: pointer; text-align: left;
  font-family: inherit; transition: border-color .15s, background .15s;
}
.rip-typebtn b { font-size: 12.5px; font-weight: 700; color: var(--ink-2); line-height: 1.25; }
.rip-typebtn small { font-size: 10px; color: var(--ink-4); }
.rip-typebtn.is-active { border-color: var(--brand); background: rgba(94,113,228,.06); }
.rip-typebtn.is-active b { color: var(--brand); }
.rip-input {
  height: 40px; padding: 9px 13px;
  border: 1px solid var(--ink-6); box-shadow: none;
  border-radius: 10px; font-size: 13.5px; color: var(--ink);
  background: #f9fafc; outline: none; width: 100%; box-sizing: border-box;
  font-family: inherit; transition: border-color .12s, box-shadow .12s, background .12s;
}
.rip-input:hover { background: #f4f6fa; }
.rip-input:focus { border-color: var(--brand); background: #fff; box-shadow: 0 0 0 3.5px rgba(94,113,228,.12); }
.rip-select {
  appearance: none; -webkit-appearance: none; -moz-appearance: none;
  padding-right: 30px; cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 10px center;
}

/* ─── Aviso del borrador — bloque de info limpio ───────────────────────────── */
.rip-draft-note {
  flex-basis: 100%; display: flex; flex-direction: column; gap: 9px;
  background: var(--ink-8); border-radius: 11px; padding: 12px 14px;
  font-size: 11.5px; color: var(--ink-4); line-height: 1.55; margin-bottom: 4px;
}
.rip-draft-note b { color: var(--ink-2); font-weight: 700; }
.rip-draft-note__lead { margin: 0; }
.rip-draft-meta {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px 18px; padding-top: 11px; border-top: 1px solid var(--ink-6);
}
.rip-draft-meta__item { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.rip-draft-meta__label {
  font-size: 9.5px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .05em; color: var(--ink-4);
}
.rip-draft-meta__value {
  display: flex; align-items: center; gap: 7px; flex-wrap: wrap;
  font-size: 13.5px; font-weight: 700; color: var(--ink);
  font-family: 'DM Mono', 'Fira Mono', 'Courier New', monospace; letter-spacing: .01em;
}
.rip-draft-meta__tag {
  font-family: 'DM Sans', system-ui, sans-serif; font-style: normal;
  font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  color: var(--ink-4); background: var(--ink-6); padding: 2px 6px; border-radius: 5px;
}
.rip-draft-note__foot { margin: 0; font-size: 10.5px; color: var(--ink-4); font-style: italic; }

/* Aviso de validación fiscal (NIF inválido, etc.) — corregir antes de emitir */
.rip-fiscal-warn {
  flex-basis: 100%; display: flex; align-items: flex-start; gap: 8px;
  background: #fffbeb; box-shadow: 0 0 0 1px #fde68a;
  border-radius: 10px; padding: 10px 13px; margin-bottom: 6px;
  font-size: 12px; color: #92400e; line-height: 1.5; font-weight: 500;
}
.rip-fiscal-warn svg { color: #d97706; }
.rip-draft-empty {
  flex-basis: 100%; display: flex; align-items: flex-start; gap: 8px;
  background: #eff6ff; box-shadow: 0 0 0 1px #bfdbfe;
  border-radius: 10px; padding: 10px 13px; margin-bottom: 6px;
  font-size: 12px; color: #1e3a8a; line-height: 1.5; font-weight: 500;
}
.rip-draft-empty svg { color: #2563eb; }
.rip-draft-empty b { font-weight: 700; }

/* ─── Checklist y notice ───────────────────────────────────────────────────── */
.rip-checklist { display: flex; flex-direction: column; gap: 8px; }
.rip-checklist__item { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: var(--ink-2); line-height: 1.4; }
.rip-checklist__item svg { flex-shrink: 0; color: var(--green); }

.rip-notice {
  position: relative;
  background: #fffbeb;
  box-shadow: 0 0 0 1px #fde68a;
  border-radius: var(--rs);
  /* Padding-left reserva el hueco del icono; el texto FLUYE como frase (no flex,
     para que "Corregir datos" no se parta en columnas). */
  padding: 9px 12px 9px 31px;
  font-size: 12px; color: #92400e; line-height: 1.5;
}
.rip-notice > svg {
  position: absolute; left: 11px; top: 10px;
}
.rip-notice strong { font-weight: 700; color: #78350f; }

/* ─── Dot adicionales ──────────────────────────────────────────────────────── */
.rip-dot--corrected { background: #d97706; }  /* amber: factura corregida */

/* ─── Dot adicional para historial ─────────────────────────────────────────── */
.rip-dot--corrected { background: #d97706; }

/* ─── Badges ───────────────────────────────────────────────────────────────── */
.rip-badge {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 10px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
  padding: 2px 8px; border-radius: 99px; white-space: nowrap;
}
/* Rectificativa: esta factura ES la corrección */
.rip-badge--correction {
  background: #ede9fe; color: #5b21b6;
}
/* Corregida: esta factura FUE corregida */
.rip-badge--corrected {
  background: #fef3c7; color: #92400e;
}

/* ─── Historial de facturas ─────────────────────────────────────────────────── */
/* ─── Facturas emitidas (listado, arriba) ──────────────────────────────────── */
.rip-issued {
  background: #fff;
  box-shadow: var(--shadow-sm);
  border-radius: var(--r); overflow: hidden;
  margin-bottom: 12px;
}
.rip-issued__title {
  display: flex; align-items: center; gap: 7px;
  margin: 0; padding: 10px 16px;
  font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: .08em; color: var(--ink-4);
  border-bottom: 1px solid var(--ink-7);
}
.rip-issued__title svg { color: var(--green); }
.rip-issued__count {
  min-width: 17px; height: 17px; padding: 0 5px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--green-lt); color: var(--green);
  border-radius: 9px; font-size: 10px; font-weight: 800; letter-spacing: 0;
}
.rip-issued__row {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 16px; border-bottom: 1px solid var(--ink-7);
}
.rip-issued__row:last-child { border-bottom: none; }
.rip-issued__row--void { opacity: .6; }
.rip-issued__body { flex: 1; min-width: 0; }
.rip-issued__head { display: flex; align-items: center; gap: 7px; flex-wrap: wrap; }
.rip-issued__client {
  margin: 6px 0 0; font-size: 13px; font-weight: 600; color: var(--ink);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.rip-issued__meta {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  margin-top: 3px; font-size: 11px; color: var(--ink-4);
}
.rip-issued__meta span:not(:first-child)::before { content: '·'; margin-right: 8px; color: var(--ink-5); }
.rip-issued__aside { display: flex; flex-direction: column; align-items: flex-end; gap: 9px; flex-shrink: 0; }
.rip-issued__total { font-size: 14px; font-weight: 700; color: var(--ink); white-space: nowrap; }
.rip-issued__actions { display: flex; align-items: center; gap: 5px; }

/* ─── Botón-icono compacto (acciones por factura del listado) ───────────────── */
.rip-icon-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border: none; cursor: pointer;
  background: var(--ink-7); color: var(--ink-3);
  border-radius: 8px; text-decoration: none;
  transition: background .15s, color .15s;
}
.rip-icon-btn:hover { background: var(--ink-6); color: var(--ink); }
.rip-icon-btn--wa:hover { background: rgba(17,177,77,.12); color: var(--wa); }
.rip-icon-btn--danger:hover { background: var(--red-lt); color: var(--red); }

/* ─── Cargo posterior → crear otra factura ─────────────────────────────────── */
.rip-next {
  display: flex; flex-direction: column; align-items: center; text-align: center;
  gap: 11px; padding: 22px 18px;
  background: #fff; box-shadow: var(--shadow-sm);
  border-radius: var(--r);
}
.rip-next__icon {
  width: 42px; height: 42px; border-radius: 12px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(94,113,228,.09); color: var(--brand);
}
.rip-next__text { max-width: 340px; }
.rip-next__title { margin: 0; font-size: 13.5px; font-weight: 700; color: var(--ink); }
.rip-next__sub { margin: 4px 0 0; font-size: 11.5px; line-height: 1.55; color: var(--ink-3); }
.rip-next__btn { margin-top: 2px; }

@media (max-width: 440px) {
  .rip-issued__row { flex-wrap: wrap; }
  .rip-issued__aside { flex-direction: row; align-items: center; justify-content: space-between; width: 100%; }

  /* Estrecho: la descripción ocupa su línea y debajo van «cant. × p. unit. · IVA» y
     el importe. Se quita la cabecera y el signo × hace de etiqueta (no es texto que
     traducir), para no dejar cuatro números sueltos sin nombre. */
  .rip-concepts__head { display: none; }
  .rip-concept { grid-template-columns: auto auto auto 1fr; gap: 6px; }
  .rip-concept__name { grid-column: 1 / -1; }
  .rip-concept__num { font-size: 11px; }
  .rip-concept > span:nth-child(2) { text-align: left; }
  .rip-concept > span:nth-child(3)::before { content: '\00d7 '; color: var(--ink-4); }
}

/* ─── Link pequeño ─────────────────────────────────────────────────────────── */
.rip-link-sm {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 11px; font-weight: 600; color: var(--brand); text-decoration: none;
  padding: 3px 9px; border-radius: 6px; background: rgba(94,113,228,.07);
  transition: all .15s; white-space: nowrap;
}
.rip-link-sm:hover { background: rgba(94,113,228,.14); }

/* ─── Caja informativa (infobox) ───────────────────────────────────────────── */
.rip-infobox {
  display: flex; align-items: flex-start; gap: 8px;
  background: #eff6ff;
  box-shadow: 0 0 0 1px #bfdbfe;
  border-radius: var(--rs); padding: 10px 13px;
  font-size: 12px; color: #1e3a8a; line-height: 1.55;
}

/* ─── Selector en formulario ───────────────────────────────────────────────── */
.rip-input--select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 30px;
  cursor: pointer;
}

/* ─── Etiqueta de sección en formulario de corrección ──────────────────────── */
.rip-section-label {
  display: flex; align-items: center; gap: 6px;
  font-size: 10.5px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .05em; color: var(--ink-4);
  padding: 6px 0 2px;
  border-top: 1px solid var(--ink-7);
  margin-top: 4px;
  svg { flex-shrink: 0; }
}

/* ─── Label opcional ───────────────────────────────────────────────────────── */
.rip-label__opt {
  font-size: 9.5px; font-weight: 500; color: var(--ink-4);
  text-transform: none; letter-spacing: 0;
}

/* ─── Transición ───────────────────────────────────────────────────────────── */
.rip-fade-enter-active, .rip-fade-leave-active { transition: opacity .18s ease; }
.rip-fade-enter, .rip-fade-leave-to { opacity: 0; }

/* ─── Responsive ───────────────────────────────────────────────────────────── */
@media (max-width: 500px) {
  .rip-empty   { flex-direction: column; align-items: flex-start; }
  .rip-card__body { flex-direction: column; gap: 12px; }
  .rip-card__total { flex-direction: row; align-items: baseline; gap: 5px; }
  .rip-total-num { font-size: 18px; }
  .rip-field   { grid-template-columns: 1fr; column-gap: 0; row-gap: 5px; }
  .rip-label   { padding-top: 0; grid-row: auto; }
  .rip-field > .rip-input { grid-column: 1; grid-row: auto; }
  .rip-modal__head { padding: 16px 18px 12px; }
  .rip-modal__body { padding: 16px 18px; }
  .rip-modal__foot { padding: 12px 18px; }
}

/* ══════════════════════════════════════════════════════════════════════════════
   CORRECTION MODAL  —  cm-* design system
   Matches ManualInvoiceCreateModal visual language exactly.
   ══════════════════════════════════════════════════════════════════════════════ */

/* ─── Overlay ──────────────────────────────────────────────────────────────── */
.cm-overlay {
  position: fixed;
  inset: 0;
  z-index: 9900;
  background: rgba(15, 23, 42, 0.52);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: 'DM Sans', system-ui, sans-serif;
}

/* ─── Container ────────────────────────────────────────────────────────────── */
.cm-container {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.18), 0 4px 16px rgba(15, 23, 42, 0.08);
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'DM Sans', system-ui, sans-serif;
}
.cm-container--sm {
  max-width: 400px;
}

/* ─── Header ───────────────────────────────────────────────────────────────── */
.cm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px 18px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  flex-shrink: 0;
}
.cm-h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 9px;
  line-height: 1.2;
}
.cm-h3__icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #ede9fe;
  color: #5e72e4;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
}
.cm-h3__icon--success {
  background: #dcfce7;
  color: #16a34a;
}
.cm-h3__code {
  font-size: 11.5px;
  font-weight: 600;
  color: #5e72e4;
  background: #eef0fd;
  border: 1px solid #c7cdf9;
  border-radius: 6px;
  padding: 2px 8px;
  letter-spacing: 0.03em;
  font-family: 'DM Mono', 'Fira Mono', monospace;
}
.cm-x {
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  border-radius: 8px;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
  transition: background .15s, color .15s;
}
.cm-x:hover { background: #f1f5f9; color: #0f172a; }
.cm-x:disabled { opacity: .45; cursor: not-allowed; }

/* ─── Body ─────────────────────────────────────────────────────────────────── */
.cm-body {
  padding: 22px 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1 1 auto;
}

/* ─── Footer ───────────────────────────────────────────────────────────────── */
.cm-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  flex-shrink: 0;
}

/* ─── Info box ─────────────────────────────────────────────────────────────── */
.cm-infobox {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  padding: 12px 14px;
  color: #1e40af;
}
.cm-infobox__icon {
  font-size: 14px;
  margin-top: 1px;
  flex-shrink: 0;
}
.cm-infobox__text {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.55;
  color: #1e40af;
}
.cm-infobox__text strong {
  font-weight: 700;
  color: #1e3a8a;
}

/* ─── Section divider ──────────────────────────────────────────────────────── */
.cm-divider {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
  margin: 2px 0;
}
.cm-divider::before,
.cm-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e2e8f0;
}
.cm-divider svg { flex-shrink: 0; color: #94a3b8; }

/* ─── Form grid ────────────────────────────────────────────────────────────── */
.cm-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 14px;
}

/* ─── Field ────────────────────────────────────────────────────────────────── */
.cm-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.cm-field--full {
  grid-column: 1 / -1;
}

/* ─── Label ────────────────────────────────────────────────────────────────── */
.cm-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 4px;
}
.cm-label__req {
  color: #ef4444;
  font-size: 13px;
  line-height: 1;
}
.cm-label__opt {
  font-size: 9.5px;
  font-weight: 500;
  color: #94a3b8;
  text-transform: none;
  letter-spacing: 0;
}

/* ─── Input ────────────────────────────────────────────────────────────────── */
.cm-input {
  height: 36px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0 11px;
  font-size: 13.5px;
  font-family: 'DM Sans', system-ui, sans-serif;
  color: #0f172a;
  background: #fff;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
  width: 100%;
  box-sizing: border-box;
}
.cm-input:focus {
  border-color: #5e72e4;
  box-shadow: 0 0 0 3px rgba(94, 114, 228, 0.14);
}
.cm-input::placeholder { color: #cbd5e1; }
.cm-input--select {
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 30px;
  cursor: pointer;
}

/* ─── Buttons ──────────────────────────────────────────────────────────────── */
.cm-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 16px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'DM Sans', system-ui, sans-serif;
  border: 1.5px solid transparent;
  cursor: pointer;
  transition: background .15s, box-shadow .15s, opacity .15s;
  white-space: nowrap;
}
.cm-btn:disabled { opacity: .5; cursor: not-allowed; }
.cm-btn--cancel {
  background: #fff;
  border-color: #e2e8f0;
  color: #334155;
}
.cm-btn--cancel:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
}
.cm-btn--save {
  background: #5e72e4;
  border-color: #5e72e4;
  color: #fff;
  box-shadow: 0 2px 8px rgba(94, 114, 228, 0.28);
}
.cm-btn--save:hover:not(:disabled) {
  background: #4a5fcf;
  border-color: #4a5fcf;
  box-shadow: 0 4px 14px rgba(94, 114, 228, 0.38);
}

/* ─── Spinner ──────────────────────────────────────────────────────────────── */
.cm-spinner {
  display: inline-block;
  width: 13px;
  height: 13px;
  border: 2px solid rgba(255,255,255,.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: cm-spin .65s linear infinite;
  flex-shrink: 0;
}
@keyframes cm-spin { to { transform: rotate(360deg); } }

/* ─── Success state ────────────────────────────────────────────────────────── */
.cm-success-code {
  text-align: center;
  font-size: 26px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: 0.04em;
  font-family: 'DM Mono', 'Fira Mono', monospace;
  background: #f0fdf4;
  border: 1.5px solid #bbf7d0;
  border-radius: 12px;
  padding: 14px 20px;
}
.cm-checklist {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.cm-checklist__item {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  font-size: 13px;
  color: #334155;
  line-height: 1.45;
}
.cm-checklist__item strong { color: #0f172a; }
.cm-check-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #dcfce7;
  border: 1.5px solid #86efac;
  flex-shrink: 0;
  margin-top: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cm-check-dot::after {
  content: '';
  display: block;
  width: 5px;
  height: 8px;
  border: 1.5px solid #16a34a;
  border-top: none;
  border-left: none;
  transform: rotate(45deg) translate(-1px, -1px);
}

/* ─── Responsive ───────────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  .cm-overlay { padding: 12px; }
  .cm-grid { grid-template-columns: 1fr; }
  .cm-field--full { grid-column: 1; }
  .cm-footer { flex-direction: column-reverse; }
  .cm-btn { width: 100%; justify-content: center; }
}
</style>
